import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FaArrowLeft,
  FaArrowRight,
  FaBell,
  FaCalendarAlt,
  FaCamera,
  FaCheck,
  FaCheckCircle,
  FaChevronRight,
  FaClipboardCheck,
  FaClock,
  FaDownload,
  FaDollarSign,
  FaEdit,
  FaExclamationTriangle,
  FaFileAlt,
  FaFilePdf,
  FaHistory,
  FaMapMarkerAlt,
  FaMoon,
  FaPaperclip,
  FaPlus,
  FaReceipt,
  FaSeedling,
  FaShieldAlt,
  FaSun,
  FaTimes,
  FaTrashAlt,
  FaUser,
} from 'react-icons/fa';
import {
  cancelCadernoOrder,
  cadernoRuntime,
  createCadernoEvidence,
  createCadernoExecution,
  createCadernoOrder,
  deleteCadernoOrder,
  deleteLegacyCadernoEvent,
  downloadCadernoEvidenceFile,
  listLegacyCadernoEvents,
  listCadernoOrders,
  updateCadernoOrder,
  updateCadernoExecutionStatus,
  updateCadernoOrderStatus,
  uploadCadernoEvidenceFile,
} from '../services/cadernoService';
import {
  attachCadernoExpenseReceipt,
  createCadernoExpense,
  isCadernoExpenseSchemaUnavailable,
  listCadernoExpenses,
  parseCadernoExpenseValue,
  deleteCadernoExpense,
  uploadCadernoExpenseReceipt,
} from '../services/cadernoExpenseService';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { usePlanLimits } from '../hooks/usePlanLimits';
import { toLocalIsoDate } from '../utils/localDate';
import {
  formatEvidenceFileSize,
  prepareEvidenceFiles,
  releaseEvidencePreview,
} from '../services/evidenceFileService';
import { createLocalOperationalNotification } from '../services/operationalNotificationService';
import { getQueue, removeFromQueue, QUEUE_UPDATED_EVENT } from '../services/offlineQueue';
import { cleanupDeletedCadernoFiles } from '../services/cadernoDeletionService';
import { classifyFieldTask, summarizeFieldTasks } from '../utils/fieldTaskSummary';
import { getFieldCreationMode } from '../utils/fieldCreationMode';
import { FIELD_EXPENSE_CATEGORIES, collectRealizedFieldCosts, formatFieldCost, sumFieldCosts, summarizeTaskCosts } from '../utils/fieldTaskCosts';
import { propertyActionAllowed, propertiesForModule } from '../utils/collaborationAccess';
import { plotMatchesLegacyId } from '../services/operationalPropertiesService';
import './FieldNotebookPreview.css';
import FieldNotebookAttachment from './FieldNotebookAttachment';
import PecuariaHistory from '../pecuaria/PecuariaHistory';

pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts;

const PROPERTIES = [
  {
    id: 'property-horizonte',
    name: 'Fazenda Horizonte',
    location: 'Três Lagoas, MS',
    plots: ['Talhão Norte', 'Talhão Central', 'Talhão Sul'],
  },
  {
    id: 'property-boa-vista',
    name: 'Sítio Boa Vista',
    location: 'Viçosa, MG',
    plots: ['Talhão Café', 'Área Experimental'],
  },
];

const RESPONSIBLES = [
  { id: 'usuario-demo', name: 'Usuário de demonstração', role: 'Gestor', initials: 'UD' },
  { id: 'lucas', name: 'Lucas Almeida', role: 'Operador de campo', initials: 'LA' },
  { id: 'marina', name: 'Marina Rocha', role: 'Técnica agronômica', initials: 'MR' },
];

const STATUS = {
  scheduled: { label: 'Planejada', tone: 'scheduled' },
  late: { label: 'Atrasada', tone: 'late' },
  in_progress: { label: 'Em execução', tone: 'progress' },
  awaiting: { label: 'Aguardando aprovação', tone: 'awaiting' },
  completed: { label: 'Concluída', tone: 'completed' },
  cancelled: { label: 'Cancelada', tone: 'cancelled' },
  sync_pending: { label: 'Aguardando conexão', tone: 'sync' },
};

const TASK_TYPES = [
  'Irrigação',
  'Vistoria de campo',
  'Análise de solo',
  'Aplicação',
  'Manutenção',
  'Monitoramento',
  'Plantio',
  'Colheita',
  'Doença ou praga',
  'Operação de campo',
  'Observação',
  'Problema ou emergência',
];

const BASE_TASKS = [
  {
    id: 'OS-2026-0042',
    title: 'Irrigar 12,6 mm antes do aumento do vento',
    type: 'Irrigação',
    property: 'Fazenda Horizonte',
    location: 'Três Lagoas, MS',
    plot: 'Talhão Norte',
    status: 'late',
    priority: 'urgent',
    due: 'Hoje · 13:30',
    dueGroup: 'today',
    source: 'alert',
    sourceLabel: 'Alerta de umidade',
    responsible: RESPONSIBLES[1],
    createdBy: 'Climorax · motor de alertas',
    description: 'Aplicar a lâmina recomendada e registrar a leitura do hidrômetro ao final da operação.',
    evidence: ['Foto do hidrômetro', 'Leitura final'],
    updates: [
      { time: 'Hoje · 08:12', title: 'Ordem criada automaticamente', detail: 'Alerta de umidade crítica convertido em atividade.' },
      { time: 'Hoje · 08:18', title: 'Responsável definido', detail: 'O gestor atribuiu a atividade para Lucas Almeida.' },
      { time: 'Hoje · 12:46', title: 'Prazo em atenção', detail: 'A atividade ainda não foi iniciada.' },
    ],
  },
  {
    id: 'OS-2026-0041',
    title: 'Vistoriar setor noroeste e registrar evidências',
    type: 'Vistoria de campo',
    property: 'Fazenda Horizonte',
    location: 'Três Lagoas, MS',
    plot: 'Talhão Norte',
    status: 'in_progress',
    priority: 'high',
    due: 'Hoje · 16:00',
    dueGroup: 'today',
    source: 'alert',
    sourceLabel: 'Alerta de satélite',
    responsible: RESPONSIBLES[2],
    createdBy: 'Usuário de demonstração',
    description: 'Verificar os 2,7 ha em atenção, fotografar pontos representativos e descrever a condição encontrada.',
    evidence: ['3 fotos georreferenciadas', 'Observação técnica'],
    updates: [
      { time: 'Hoje · 07:55', title: 'Ordem criada', detail: 'Criada a partir da recomendação do sensoriamento remoto.' },
      { time: 'Hoje · 08:40', title: 'Execução iniciada', detail: 'Marina iniciou a vistoria pelo aplicativo móvel.' },
    ],
  },
  {
    id: 'OS-2026-0040',
    title: 'Coletar amostras para análise de solo',
    type: 'Análise de solo',
    property: 'Sítio Boa Vista',
    location: 'Viçosa, MG',
    plot: 'Talhão Café',
    status: 'scheduled',
    priority: 'normal',
    due: 'Amanhã · 08:00',
    dueGroup: 'upcoming',
    source: 'manual',
    sourceLabel: 'Criada manualmente',
    responsible: RESPONSIBLES[2],
    createdBy: 'Usuário de demonstração',
    description: 'Realizar coleta nas profundidades de 0–20 cm e 20–40 cm seguindo o plano de amostragem.',
    evidence: ['Foto dos pontos', 'Comprovante do laboratório'],
    updates: [
      { time: 'Ontem · 17:26', title: 'Ordem criada manualmente', detail: 'O gestor definiu propriedade, talhão e responsável.' },
      { time: 'Ontem · 17:28', title: 'Notificação enviada', detail: 'Marina recebeu a atividade no aplicativo.' },
    ],
  },
  {
    id: 'OS-2026-0039',
    title: 'Revisar linhas de gotejamento do setor 2',
    type: 'Manutenção',
    property: 'Fazenda Horizonte',
    location: 'Três Lagoas, MS',
    plot: 'Talhão Central',
    status: 'awaiting',
    priority: 'normal',
    due: 'Executada hoje · 10:18',
    dueGroup: 'approval',
    source: 'manual',
    sourceLabel: 'Criada manualmente',
    responsible: RESPONSIBLES[1],
    createdBy: 'Usuário de demonstração',
    description: 'Inspecionar emissores, corrigir vazamentos e registrar as peças utilizadas.',
    evidence: ['3 fotos anexadas', 'R$ 186,00 em materiais'],
    execution: {
      summary: 'Foram corrigidos dois vazamentos e substituídos quatro conectores da linha principal.',
      notes: 'Fluxo restabelecido e pressão conferida ao final do setor.',
      actualPeriod: 'Hoje · 08:03–10:18',
      photos: 3,
      document: true,
      locationConfirmed: true,
      cost: '186,00',
      executor: 'Lucas Almeida',
    },
    updates: [
      { time: '11 ago · 15:10', title: 'Ordem criada manualmente', detail: 'Atividade atribuída para Lucas Almeida.' },
      { time: 'Hoje · 08:03', title: 'Execução iniciada', detail: 'Registro realizado em campo.' },
      { time: 'Hoje · 10:18', title: 'Execução enviada para aprovação', detail: '3 fotos e custo de materiais anexados.' },
    ],
  },
  {
    id: 'OS-2026-0038',
    title: 'Monitorar estabelecimento das mudas',
    type: 'Monitoramento',
    property: 'Sítio Boa Vista',
    location: 'Viçosa, MG',
    plot: 'Área Experimental',
    status: 'completed',
    priority: 'normal',
    due: 'Concluída em 11 ago · 16:42',
    dueGroup: 'completed',
    source: 'planning',
    sourceLabel: 'Planejamento da safra',
    responsible: RESPONSIBLES[1],
    createdBy: 'Planejamento da safra',
    description: 'Avaliar uniformidade, falhas e sinais de estresse no estabelecimento inicial.',
    evidence: ['5 fotos', 'Relatório de campo'],
    execution: {
      summary: 'Monitoramento concluído em toda a área experimental, com 94% de estabelecimento uniforme.',
      notes: 'Falhas concentradas na bordadura sul e já sinalizadas para replantio.',
      actualPeriod: '11 ago · 14:12–16:20',
      photos: 5,
      document: true,
      locationConfirmed: true,
      cost: '0,00',
      executor: 'Lucas Almeida',
    },
    updates: [
      { time: '08 ago · 09:00', title: 'Ordem programada', detail: 'Gerada pelo planejamento da safra.' },
      { time: '11 ago · 14:12', title: 'Execução iniciada', detail: 'Lucas iniciou o monitoramento.' },
      { time: '11 ago · 16:42', title: 'Atividade aprovada', detail: 'O gestor revisou as evidências e aprovou o registro.' },
    ],
  },
];

const FILTERS = [
  { key: 'all', label: 'Todas' },
  { key: 'today', label: 'Hoje' },
  { key: 'late', label: 'Atrasadas' },
  { key: 'approval', label: 'Para aprovar' },
  { key: 'completed', label: 'Concluídas' },
  { key: 'cancelled', label: 'Canceladas' },
];

const PERMISSION_LABELS = {
  owner: 'Proprietário',
  editor: 'Editor',
  viewer: 'Visualizador',
};

const DATABASE_STATUS = {
  rascunho: 'scheduled',
  planejada: 'scheduled',
  em_execucao: 'in_progress',
  aguardando_aprovacao: 'awaiting',
  concluida: 'completed',
  cancelada: 'cancelled',
};

const DATABASE_PRIORITY = {
  baixa: 'normal',
  normal: 'normal',
  alta: 'high',
  urgente: 'urgent',
};

const UI_PRIORITY = {
  normal: 'normal',
  high: 'alta',
  urgent: 'urgente',
};

const UI_TYPE_TO_DATABASE = {
  'Irrigação': 'irrigacao',
  'Vistoria de campo': 'monitoramento',
  'Análise de solo': 'analise',
  'Aplicação': 'aplicacao',
  'Manutenção': 'manutencao',
  'Monitoramento': 'monitoramento',
  'Plantio': 'plantio',
  'Colheita': 'colheita',
  'Doença ou praga': 'doenca',
  'Operação de campo': 'operacao',
  'Observação': 'observacao',
  'Problema ou emergência': 'problema',
};

const DATABASE_TYPE_TO_UI = Object.entries(UI_TYPE_TO_DATABASE).reduce((result, [label, value]) => {
  result[value] = label;
  return result;
}, {});

const LEGACY_COST_LABELS = {
  produto: 'Produto ou insumo',
  mao_de_obra: 'Mão de obra',
  maquinario: 'Maquinário',
  outros: 'Outros',
  energia: 'Energia elétrica',
  manutencao: 'Manutenção',
  laudo: 'Laudo ou análise',
  coleta: 'Coleta',
  pecas: 'Peças ou materiais',
};

function initials(name) {
  return String(name || 'Usuário')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('') || 'US';
}

function createClientRequestId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
    const random = Math.floor(Math.random() * 16);
    const value = character === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

function formatDateTime(value, fallback = 'Sem prazo definido') {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function historyTitle(item) {
  if (item.dados?.despesa_registrada) return 'Gasto registrado';
  if (item.dados?.despesa_estornada) return 'Gasto estornado';
  if (item.acao === 'status_alterado') return 'Status da ordem atualizado';
  if (item.acao === 'status_execucao_alterado') return 'Execução atualizada';
  if (item.entidade === 'evidencia') return 'Evidência registrada';
  if (item.entidade === 'execucao') return 'Execução registrada';
  if (item.acao === 'criada') return 'Ordem criada';
  return 'Registro atualizado';
}

function mapDatabaseOrderToTask(order, properties, currentUserId, currentUserName) {
  const property = properties.find(item => String(item.id) === String(order.propriedade_id));
  const plotRecord = property?.plotRecords?.find(item => plotMatchesLegacyId(item, order.talhao_id));
  const plannedDate = order.inicio_previsto ? new Date(order.inicio_previsto) : null;
  const normalizedStatus = DATABASE_STATUS[order.status] || 'scheduled';
  const status = normalizedStatus === 'scheduled'
    && plannedDate
    && plannedDate.getTime() < Date.now()
    ? 'late'
    : normalizedStatus;
  const responsibleName = order.responsible?.nome || order.responsible?.email || 'Responsável não identificado';
  const creatorName = order.creator?.nome || order.creator?.email || currentUserName;
  const latestExecution = order.executions?.[0] || null;
  const executionEvidence = latestExecution
    ? order.evidence.filter(item => String(item.execucao_id) === String(latestExecution.id))
    : order.evidence;
  const photos = executionEvidence.filter(item => item.tipo === 'foto').length;
  const documents = executionEvidence.some(item => item.tipo === 'documento');
  const history = [...(order.history || [])].reverse().map(item => {
    const actor = order.users?.[String(item.ator_id)];
    return {
      time: formatDateTime(item.ocorrido_em),
      title: historyTitle(item),
      detail: actor?.nome ? `Registro realizado por ${actor.nome}.` : 'Registro protegido pelo histórico do Caderno.',
    };
  });
  const attachments = (order.evidence || []).map(item => ({
    id: item.id,
    type: item.tipo,
    title: item.titulo,
    name: item.nome_original || item.titulo,
    path: item.arquivo_path,
    mimeType: item.mime_type,
    originalMimeType: item.mime_type_original || item.mime_type,
    size: item.tamanho_bytes,
    originalSize: item.tamanho_original_bytes || item.tamanho_bytes,
    compression: item.compressao,
    createdAt: item.criado_em,
  }));
  const canModifyProperty = propertyActionAllowed(property, 'caderno', 'edit', ['owner', 'editor'].includes(property?.permission));
  const isOwner = property?.permission === 'owner';
  const isResponsible = String(order.responsavel_id) === String(currentUserId);

  return {
    id: order.codigo,
    recordId: order.id,
    version: order.versao,
    plannedCost: order.custo_previsto,
    costExecutions: order.executions || [],
    title: order.titulo,
    type: DATABASE_TYPE_TO_UI[order.tipo] || order.tipo,
    property: property?.name || 'Propriedade não identificada',
    propertyId: order.propriedade_id,
    location: property?.location || 'Localização não informada',
    plot: plotRecord?.name || (order.talhao_id == null ? 'Toda a propriedade' : 'Talhão não identificado'),
    plotId: order.talhao_id,
    permission: property?.permission || 'viewer',
    status,
    databaseStatus: order.status,
    priority: DATABASE_PRIORITY[order.prioridade] || 'normal',
    due: formatDateTime(order.inicio_previsto),
    plannedAt: order.inicio_previsto,
    dueGroup: classifyFieldTask({ status, plannedAt: order.inicio_previsto }),
    source: order.origem_tipo === 'alerta' ? 'alert' : order.origem_tipo === 'planejamento' ? 'planning' : 'manual',
    sourceLabel: order.origem_tipo === 'alerta' ? 'Alerta operacional' : order.origem_tipo === 'planejamento' ? 'Planejamento' : 'Criada manualmente',
    responsible: {
      id: order.responsavel_id,
      name: responsibleName,
      role: 'Responsável pela execução',
      initials: initials(responsibleName),
    },
    createdBy: creatorName,
    description: order.descricao || 'Sem instruções adicionais.',
    evidence: order.evidencias_exigidas?.length ? order.evidencias_exigidas : ['Registro de conclusão'],
    attachments,
    cancellationReason: order.motivo_cancelamento || '',
    cancelledAt: order.cancelado_em || null,
    execution: latestExecution ? {
      summary: latestExecution.resumo || 'Execução registrada.',
      notes: latestExecution.observacoes || '',
      actualPeriod: `${formatDateTime(latestExecution.inicio_em)} – ${formatDateTime(latestExecution.fim_em, 'em andamento')}`,
      photos,
      document: documents,
      locationConfirmed: latestExecution.latitude != null && latestExecution.longitude != null,
      cost: latestExecution.custo_real == null ? null : Number(latestExecution.custo_real).toLocaleString('pt-BR', { minimumFractionDigits: 2 }),
      executor: order.users?.[String(latestExecution.executor_id)]?.nome || responsibleName,
    } : null,
    updates: history.length ? history : [{
      time: formatDateTime(order.criado_em),
      title: 'Ordem criada',
      detail: `Atividade vinculada a ${property?.name || 'uma propriedade'}${plotRecord ? ` e ${plotRecord.name}` : ' em âmbito geral'}.`,
    }],
    canExecute: (isOwner || isResponsible) && propertyActionAllowed(property, 'caderno', 'execute', canModifyProperty),
    canApprove: propertyActionAllowed(property, 'caderno', 'approve', isOwner),
    canEdit: canModifyProperty && ['scheduled', 'late', 'in_progress'].includes(status),
    canCancel: canModifyProperty && !['completed', 'cancelled'].includes(status),
    canDelete: canModifyProperty && propertyActionAllowed(property, 'caderno', 'delete', ['owner', 'editor'].includes(property?.permission)),
    canAttach: status !== 'cancelled' && canModifyProperty,
    isPersisted: true,
  };
}

function legacyCostDetails(value) {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function legacyEventAttachments(event) {
  const attachments = Array.isArray(event?.anexos) ? event.anexos : [];
  return attachments.map((attachment, index) => {
    const name = attachment?.nome || `Anexo ${index + 1}`;
    const isDocument = /\.pdf$/i.test(name) || String(attachment?.mime_type || '').includes('pdf');
    return {
      id: `evento-${event.id}-anexo-${index}`,
      type: isDocument ? 'documento' : 'foto',
      name,
      legacyUrl: attachment?.url || null,
      legacyPath: attachment?.path || null,
      size: Number(attachment?.tamanho_bytes || 0),
      originalSize: Number(attachment?.tamanho_bytes || 0),
      compression: 'legado-preservado',
    };
  });
}

function mapLegacyEventToTask(event, properties, currentUserId, currentUserName) {
  const property = properties.find(item => item.plotRecords?.some(plot => plotMatchesLegacyId(plot, event.talhao_id)));
  const plotRecord = property?.plotRecords?.find(plot => plotMatchesLegacyId(plot, event.talhao_id));
  if (!property || !plotRecord) return null;

  const attachments = legacyEventAttachments(event);
  const costDetails = legacyCostDetails(event.custo_detalhes);
  const detailRows = [
    event.produto ? { label: 'Produto ou insumo', value: event.produto } : null,
    event.dose ? { label: 'Dose ou quantidade', value: event.dose } : null,
    ...Object.entries(costDetails)
      .filter(([, value]) => value !== '' && value != null)
      .map(([key, value]) => ({
        label: LEGACY_COST_LABELS[key] || key.replace(/_/g, ' '),
        value: Number.isFinite(Number(value))
          ? Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
          : String(value),
      })),
  ].filter(Boolean);
  const createdBy = String(event.user_id) === String(currentUserId)
    ? currentUserName
    : 'Usuário registrado no evento';
  const isOffline = Boolean(event._offline);
  const eventDate = event.data_evento || event.created_at || new Date().toISOString();
  const cost = event.custo_total != null && Number.isFinite(Number(event.custo_total))
    ? Number(event.custo_total).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
    : null;
  const photos = attachments.filter(item => item.type === 'foto').length;
  const documents = attachments.some(item => item.type === 'documento');

  return {
    id: isOffline ? `EVENTO-OFFLINE-${event.id}` : `EVENTO-${event.id}`,
    recordId: event.id,
    legacyCost: event.custo_total,
    title: event.titulo || 'Registro histórico do talhão',
    type: DATABASE_TYPE_TO_UI[event.tipo] || event.tipo || 'Registro de campo',
    property: property.name,
    propertyId: property.id,
    location: property.location || 'Localização não informada',
    plot: plotRecord.name,
    plotId: plotRecord.id,
    permission: property.permission || 'viewer',
    status: isOffline ? 'sync_pending' : 'completed',
    databaseStatus: isOffline ? 'aguardando_sincronizacao' : 'historico_legado',
    priority: event.tipo === 'problema' ? 'urgent' : 'normal',
    due: formatDateTime(eventDate),
    plannedAt: eventDate,
    dueGroup: isOffline ? 'upcoming' : 'completed',
    source: 'event',
    sourceLabel: isOffline ? 'Evento preservado · aguardando conexão' : 'Evento histórico consolidado',
    responsible: {
      id: event.user_id || 'legacy-event',
      name: 'Atividade já realizada',
      role: 'Registro histórico',
      initials: 'RH',
    },
    createdBy,
    description: event.descricao || 'Registro realizado anteriormente no histórico do talhão.',
    evidence: attachments.length
      ? [`${attachments.length} anexo(s) original(is) preservado(s)`]
      : ['Dados originais preservados sem alteração'],
    attachments,
    legacyDetails: detailRows,
    execution: {
      summary: event.descricao || event.titulo || 'Atividade registrada no histórico do talhão.',
      notes: [event.produto && `Produto: ${event.produto}`, event.dose && `Dose: ${event.dose}`].filter(Boolean).join(' · '),
      actualPeriod: formatDateTime(eventDate),
      photos,
      document: documents,
      locationConfirmed: false,
      cost,
      executor: createdBy,
    },
    updates: [
      {
        time: formatDateTime(event.created_at || eventDate),
        title: isOffline ? 'Registro preservado na fila offline' : 'Evento histórico preservado no Caderno',
        detail: isOffline
          ? 'O registro continuará na fila segura e será sincronizado quando a conexão retornar.'
          : 'Os dados permanecem na tabela original e agora são apresentados de forma centralizada no Caderno de Campo.',
      },
      ...(!isOffline && event.updated_at && event.updated_at !== event.created_at ? [{
        time: formatDateTime(event.updated_at),
        title: 'Última atualização do registro original',
        detail: 'A versão mais recente do evento foi carregada sem modificar o dado histórico.',
      }] : []),
    ],
    canExecute: false,
    canApprove: false,
    canEdit: false,
    canCancel: false,
    canDelete: propertyActionAllowed(property, 'propriedades', 'edit', ['owner', 'editor'].includes(property.permission))
      && propertyActionAllowed(property, 'propriedades', 'delete', ['owner', 'editor'].includes(property.permission)),
    canAttach: false,
    isPersisted: false,
    isLegacyEvent: true,
    isOfflineLegacyEvent: isOffline,
  };
}

function contextualizeDemoTasks(properties, currentUserId, currentUserName) {
  if (!properties.length) return BASE_TASKS;

  return BASE_TASKS.map((task, index) => {
    const property = properties[index % properties.length];
    const plot = property.plots[index % Math.max(property.plots.length, 1)] || 'Sem talhão cadastrado';
    const responsible = property.collaborators?.find(person => String(person.id) === String(currentUserId))
      || property.collaborators?.[0]
      || {
        id: currentUserId || 'authenticated-user',
        name: currentUserName,
        role: 'Usuário autenticado',
        initials: initials(currentUserName),
      };

    return {
      ...task,
      property: property.name,
      location: property.location || 'Localização não informada',
      plot,
      permission: property.permission || 'viewer',
      canEdit: property.permission !== 'viewer' && ['scheduled', 'late', 'in_progress'].includes(task.status),
      canCancel: property.permission !== 'viewer' && !['completed', 'cancelled'].includes(task.status),
      canDelete: propertyActionAllowed(property, 'caderno', 'edit', ['owner', 'editor'].includes(property.permission))
        && propertyActionAllowed(property, 'caderno', 'delete', ['owner', 'editor'].includes(property.permission)),
      canAttach: task.status !== 'cancelled' && (
        property.permission === 'owner'
        || (String(responsible.id) === String(currentUserId) && ['scheduled', 'late', 'in_progress'].includes(task.status))
      ),
      canExecute: property.permission === 'owner' || String(responsible.id) === String(currentUserId),
      canApprove: property.permission === 'owner',
      responsible: {
        ...responsible,
        initials: responsible.initials || initials(responsible.name),
      },
      execution: task.execution ? { ...task.execution, executor: currentUserName } : undefined,
      updates: task.updates.map(update => ({
        ...update,
        detail: update.detail.replace(/Usuário de demonstração|Lucas Almeida|Marina Rocha/g, currentUserName),
      })),
      createdBy: task.createdBy === 'Usuário de demonstração' ? currentUserName : task.createdBy,
    };
  });
}

function sourceIcon(source) {
  if (source === 'alert') return <FaBell />;
  if (source === 'planning') return <FaCalendarAlt />;
  if (source === 'event') return <FaHistory />;
  return <FaUser />;
}

function TaskCard({ task, selected, onSelect, onAdvance }) {
  const status = STATUS[task.status];
  const readOnly = task.permission === 'viewer' && !task.canExecute && !task.canApprove;
  const approvalLocked = task.status === 'awaiting' && task.canApprove === false;
  const executionLocked = ['scheduled', 'late', 'in_progress'].includes(task.status) && task.canExecute === false;
  const actionLabel = task.isLegacyEvent
    ? (task.isOfflineLegacyEvent ? 'Aguardando conexão' : 'Ver registro preservado')
    : readOnly
    ? 'Somente leitura'
    : approvalLocked
      ? 'Aguardando proprietário'
      : executionLocked
        ? 'Atribuída a outro responsável'
    : task.status === 'awaiting'
    ? 'Revisar e aprovar'
    : task.status === 'in_progress'
      ? 'Registrar conclusão'
      : ['completed', 'cancelled'].includes(task.status)
        ? 'Ver registro'
        : 'Iniciar atividade';

  return (
    <article className={`field-task-card is-${status.tone} ${selected ? 'is-selected' : ''}`}>
      <button type="button" className="field-task-card-main" onClick={() => onSelect(task)}>
        <div className="field-task-card-topline">
          <span className={`field-task-priority is-${task.priority}`}>
            {task.priority === 'urgent' ? 'Urgente' : task.priority === 'high' ? 'Alta prioridade' : 'Prioridade normal'}
          </span>
          <span className={`field-task-status is-${status.tone}`}>{status.label}</span>
        </div>

        <div className="field-task-source">{sourceIcon(task.source)} {task.sourceLabel}</div>
        <h2>{task.title}</h2>

        <div className="field-task-hierarchy">
          <span>{task.property}</span><FaChevronRight /><strong>{task.plot}</strong>
        </div>

        <div className="field-task-meta">
          <span><FaClock /> {task.due}</span>
          <span><FaUser /> {task.responsible.name}</span>
          <span><FaCamera /> {task.isLegacyEvent ? `${task.attachments?.length || 0} anexo(s) preservado(s)` : `${task.evidence.length} evidências solicitadas`}</span>
        </div>
      </button>

      <div className="field-task-card-actions">
        <button type="button" className="field-task-trace" onClick={() => onSelect(task)}>
          <FaHistory /> Rastreabilidade
        </button>
        <button type="button" className="field-task-advance" onClick={() => onAdvance(task.id)} disabled={task.isOfflineLegacyEvent || readOnly || approvalLocked || executionLocked}>
          {actionLabel} <FaArrowRight />
        </button>
      </div>
    </article>
  );
}

export function TaskDetail({ task, accountLabel, expenses, expensesError, canAddExpense, canDeleteExpense, onDeleteExpense, onSpend, onClose, onAdvance, onEdit, onCancel, onDelete, onAttach, onDownload, detailRef }) {
  if (!task) return null;
  const costs = summarizeTaskCosts(task, expenses);
  const status = STATUS[task.status];
  const approvalLocked = task.status === 'awaiting' && task.canApprove === false;
  const executionLocked = ['scheduled', 'late', 'in_progress'].includes(task.status) && task.canExecute === false;

  return (
    <aside ref={detailRef} tabIndex="-1" className="field-task-detail" aria-label="Rastreabilidade da ordem de serviço">
      <div className="field-task-detail-heading">
        <div>
          <span className="field-notebook-kicker">Rastreabilidade</span>
          <strong>{task.id}</strong>
        </div>
        <button type="button" onClick={onClose} aria-label="Fechar detalhes"><FaTimes /></button>
      </div>

      <span className={`field-task-status is-${status.tone}`}>{status.label}</span>
      <h2>{task.title}</h2>
      <p>{task.description}</p>

      {task.isLegacyEvent ? (
        <div className="field-task-legacy-notice">
          <FaShieldAlt />
          <div><strong>Registro anterior consolidado</strong><span>Este evento pertence ao histórico original do talhão. A exclusão também remove o registro original e seus anexos.</span></div>
        </div>
      ) : (
        <div className="field-task-management-actions" aria-label="Ações da tarefa">
          {task.status !== 'cancelled' && (
            <>
              <button type="button" onClick={() => onEdit(task)} disabled={task.canEdit === false}><FaEdit /> Editar</button>
              <button type="button" onClick={() => onAttach(task)} disabled={task.canAttach === false}><FaPaperclip /> Adicionar anexo</button>
              <button type="button" className="is-danger" onClick={() => onCancel(task)} disabled={task.canCancel === false}><FaTrashAlt /> Retirar atividade</button>
            </>
          )}
          {task.canDelete && <div className="field-task-management-actions"><button type="button" className="is-danger" onClick={() => onDelete(task)}><FaTrashAlt /> Excluir definitivamente</button></div>}
        </div>
      )}

      <div className="field-task-identity">
        <div><span>Conta</span><strong>{accountLabel}</strong></div>
        <FaChevronRight />
        <div><span>Propriedade</span><strong>{task.property}</strong></div>
        <FaChevronRight />
        <div><span>Área de execução</span><strong>{task.plot}</strong></div>
      </div>

      <div className="field-task-people">
        <div>
          <span className="field-task-avatar">{initials(task.createdBy)}</span>
          <div><small>Criada por</small><strong>{task.createdBy}</strong></div>
        </div>
        <div>
          <span className="field-task-avatar is-responsible">{task.responsible.initials}</span>
          <div><small>Responsável</small><strong>{task.responsible.name}</strong><em>{task.responsible.role}</em></div>
        </div>
      </div>

      <div className="field-task-evidence">
        <span className="field-notebook-kicker">{task.isLegacyEvent ? 'Preservação do registro' : 'Comprovações solicitadas'}</span>
        {task.evidence.map(item => <div key={item}><FaCheck /> {item}</div>)}
      </div>

      {!task.isLegacyEvent && <section className="field-task-costs" aria-label="Gastos da atividade">
        <div className="field-task-costs-heading">
          <h3>Gastos da atividade</h3>
          {canAddExpense && <button type="button" onClick={() => onSpend(task)}><FaPlus /> Registrar gasto</button>}
        </div>
        <dl className="field-task-cost-totals">
          <div><dt>Previsto total</dt><dd>{formatFieldCost(costs.plannedTotal)}</dd></div>
          <div><dt>Realizado</dt><dd>{expensesError ? 'Indisponível' : formatFieldCost(costs.realizedTotal)}</dd></div>
        </dl>
        {expensesError ? <p role="alert">Não foi possível carregar os gastos. Atualize antes de registrar outro lançamento.</p> : <>
          {costs.legacyTotal > 0 && <p className="field-task-legacy-cost">Custo anterior de execução: <strong>{formatFieldCost(costs.legacyTotal)}</strong></p>}
          {costs.expenses.length > 0 ? <ul className="field-task-cost-list">{costs.expenses.map(expense => <li key={expense.id} className={expense.estornado_em ? 'is-reversed' : ''}>
            <div><strong>{expense.descricao}</strong><small>{FIELD_EXPENSE_CATEGORIES[expense.categoria] || 'Outros'} · {new Date(`${expense.data_despesa}T12:00:00`).toLocaleDateString('pt-BR')}</small>{expense.estornado_em && <small>Estornado: {expense.motivo_estorno}</small>}</div>
            <div className="field-task-cost-actions"><strong>{formatFieldCost(expense.valor)}</strong>{canDeleteExpense && <button type="button" title="Excluir gasto" aria-label={`Excluir ${expense.descricao}`} onClick={() => onDeleteExpense(expense)}><FaTrashAlt /> Excluir</button>}</div>
          </li>)}</ul> : <p>Nenhum lançamento de gasto nesta atividade.</p>}
        </>}
      </section>}

      {task.legacyDetails?.length > 0 && (
        <div className="field-task-legacy-details">
          <span className="field-notebook-kicker">Informações do evento original</span>
          {task.legacyDetails.map(item => <div key={`${item.label}-${item.value}`}><span>{item.label}</span><strong>{item.value}</strong></div>)}
        </div>
      )}

      <div className="field-task-attachments">
        <div className="field-task-attachments-heading">
          <span className="field-notebook-kicker">Fotos e documentos anexados</span>
          <strong>{task.attachments?.length || 0}</strong>
        </div>
        {task.attachments?.length ? task.attachments.map(attachment => (
          <FieldNotebookAttachment key={attachment.id} attachment={attachment} onDownload={onDownload} />
        )) : <p>Nenhum arquivo anexado a esta tarefa.</p>}
      </div>

      {task.status === 'cancelled' && (
        <div className="field-task-cancellation">
          <FaShieldAlt />
          <div><strong>Tarefa excluída da operação</strong><span>{task.cancellationReason || 'Cancelamento anterior à exigência de justificativa.'}</span></div>
        </div>
      )}

      {task.execution && (
        <div className="field-task-execution-summary">
          <div className="field-task-execution-title">
            <span className="field-notebook-kicker">Execução registrada</span>
            <strong>{task.execution.actualPeriod}</strong>
          </div>
          <p>{task.execution.summary}</p>
          <div>
            <span><FaCamera /> {task.execution.photos} foto(s)</span>
            <span><FaFileAlt /> {task.execution.document ? 'Documento anexado' : 'Sem documento'}</span>
            <span><FaMapMarkerAlt /> {task.execution.locationConfirmed ? 'Localização confirmada' : 'Sem localização'}</span>
          </div>
          <small>Executado por {task.execution.executor}{task.isLegacyEvent ? ` · Custo informado: ${task.execution.cost == null ? 'não informado' : `R$ ${task.execution.cost}`}` : ''}</small>
        </div>
      )}

      <div className="field-task-timeline">
        <span className="field-notebook-kicker">Histórico da atividade</span>
        {task.updates.map((update, index) => (
          <div className="field-task-timeline-item" key={`${update.time}-${index}`}>
            <span />
            <div><small>{update.time}</small><strong>{update.title}</strong><p>{update.detail}</p></div>
          </div>
        ))}
      </div>

      <div className="field-task-detail-footer">
        <span><FaShieldAlt /> Histórico protegido contra alterações silenciosas</span>
        <button type="button" onClick={() => onAdvance(task.id)} disabled={task.isOfflineLegacyEvent || (task.permission === 'viewer' && !task.canExecute && !task.canApprove) || approvalLocked || executionLocked}>
          {task.isLegacyEvent
            ? (task.isOfflineLegacyEvent ? 'Aguardando sincronização' : 'Registro histórico preservado')
            : task.permission === 'viewer' && !task.canExecute && !task.canApprove
            ? 'Somente visualização'
            : approvalLocked
              ? 'Aguardando proprietário'
              : executionLocked
                ? 'Atribuída a outro responsável'
            : task.status === 'awaiting'
            ? 'Revisar e aprovar'
            : task.status === 'in_progress'
              ? 'Registrar conclusão'
              : ['completed', 'cancelled'].includes(task.status)
                ? 'Abrir registro'
                : 'Iniciar atividade'}
        </button>
      </div>
    </aside>
  );
}

function CreateTaskModal({ sourceAlert, creationMode = 'order', properties, initialPropertyId = null, initialPlotId = null, onClose, onCreate, willReturnToMap = false }) {
  const alertPreset = sourceAlert === 'soil-critical';
  const [selectedMode, setSelectedMode] = useState(alertPreset ? 'order' : creationMode);
  const resolvedCreationMode = alertPreset ? 'order' : selectedMode;
  const creationCopy = getFieldCreationMode(resolvedCreationMode);
  const initialProperty = properties.find(item => String(item.id) === String(initialPropertyId)) || properties[0];
  const initialPlot = initialProperty.plotRecords.find(item => plotMatchesLegacyId(item, initialPlotId)) || initialProperty.plotRecords[0];
  const initialResponsible = initialProperty.collaborators[0];
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    property: String(initialProperty.id),
    plot: initialPlot ? String(initialPlot.id) : '',
    type: alertPreset ? 'Irrigação' : 'Vistoria de campo',
    title: alertPreset ? 'Irrigar 12,6 mm antes do aumento do vento' : '',
    description: alertPreset ? 'Aplicar a lâmina recomendada e registrar a leitura final.' : '',
    responsible: initialResponsible ? String(initialResponsible.id) : '',
    dueDate: toLocalIsoDate(),
    dueTime: alertPreset ? '13:30' : '08:00',
    priority: alertPreset ? 'urgent' : 'normal',
    recurring: 'none',
    evidence: alertPreset || creationCopy.evidenceDefault,
  });

  const selectedProperty = properties.find(item => String(item.id) === form.property) || initialProperty;
  const selectedResponsibles = selectedProperty.collaborators || [];

  const update = (field, value) => {
    const nextProperty = field === 'property'
      ? properties.find(item => String(item.id) === value)
      : null;
    setForm(current => ({
      ...current,
      [field]: value,
      ...(nextProperty ? {
        plot: nextProperty.plotRecords[0] ? String(nextProperty.plotRecords[0].id) : '',
        responsible: nextProperty.collaborators[0] ? String(nextProperty.collaborators[0].id) : '',
      } : {}),
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.title.trim() || submitting) return;
    setSubmitting(true);
    try {
      await onCreate(form, alertPreset, resolvedCreationMode);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="field-task-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <form className="field-task-modal" role="dialog" aria-modal="true" aria-labelledby="create-task-title" onSubmit={submit} onMouseDown={event => event.stopPropagation()}>
        <div className="field-task-modal-heading">
          <div>
            <span className="field-notebook-kicker">{alertPreset ? 'Origem · alerta operacional' : creationCopy.kicker}</span>
            <h2 id="create-task-title">{alertPreset ? 'Transformar alerta em ordem de serviço' : creationCopy.title}</h2>
            {!alertPreset && <p className="field-task-modal-help">{creationCopy.help}</p>}
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar formulário"><FaTimes /></button>
        </div>

        {alertPreset && (
          <div className="field-task-alert-context"><FaBell /><div><strong>Umidade crítica no Talhão Norte</strong><span>Os dados do alerta foram usados para preencher a atividade. Você pode ajustá-los antes de salvar.</span></div></div>
        )}

        {!alertPreset && (
          <div className="field-task-mode-picker" aria-label="Tipo de fluxo da atividade">
            <button type="button" className={resolvedCreationMode === 'task' ? 'is-active' : ''} aria-pressed={resolvedCreationMode === 'task'} onClick={() => setSelectedMode('task')}>
              <FaCheckCircle /><span><strong>Simples</strong><small>Concluir sem aprovação</small></span>
            </button>
            <button type="button" className={resolvedCreationMode === 'order' ? 'is-active' : ''} aria-pressed={resolvedCreationMode === 'order'} onClick={() => setSelectedMode('order')}>
              <FaShieldAlt /><span><strong>Com aprovação</strong><small>Exigir conferência do proprietário</small></span>
            </button>
          </div>
        )}

        <div className="field-task-form-grid">
          <label>
            <span>Propriedade *</span>
            <select value={form.property} onChange={event => update('property', event.target.value)}>
              {properties.map(item => <option key={item.id} value={String(item.id)}>{item.name} · {PERMISSION_LABELS[item.permission] || 'Acesso permitido'}</option>)}
            </select>
          </label>
          <label>
            <span>Talhão <small>(opcional)</small></span>
            <select value={form.plot} onChange={event => update('plot', event.target.value)}>
              <option value="">Toda a propriedade</option>
              {selectedProperty.plotRecords.map(plot => <option value={String(plot.id)} key={plot.id}>{plot.name}</option>)}
            </select>
          </label>
          <label>
            <span>Responsável *</span>
            <select value={form.responsible} onChange={event => update('responsible', event.target.value)}>
              {selectedResponsibles.map(person => <option value={String(person.id)} key={person.id}>{person.name} · {person.role}</option>)}
            </select>
          </label>
          <label className="is-wide">
            <span>Título da atividade *</span>
            <input value={form.title} onChange={event => update('title', event.target.value)} placeholder="Ex.: Realizar coleta de solo" required />
          </label>
          <label>
            <span>Data prevista *</span>
            <input type="date" value={form.dueDate} onChange={event => update('dueDate', event.target.value)} required />
          </label>
          <label>
            <span>Horário</span>
            <input type="time" value={form.dueTime} onChange={event => update('dueTime', event.target.value)} />
          </label>
        </div>

        <details className="field-task-advanced">
          <summary>Mais opções</summary>
          <div className="field-task-form-grid">
            <label>
              <span>Tipo de atividade</span>
              <select value={form.type} onChange={event => update('type', event.target.value)}>
                {TASK_TYPES.map(type => <option key={type}>{type}</option>)}
              </select>
            </label>
            <label>
              <span>Prioridade</span>
              <select value={form.priority} onChange={event => update('priority', event.target.value)}>
                <option value="normal">Normal</option>
                <option value="high">Alta</option>
                <option value="urgent">Urgente</option>
              </select>
            </label>
            <label className="is-wide">
              <span>Instruções para execução</span>
              <textarea value={form.description} onChange={event => update('description', event.target.value)} placeholder="Detalhes opcionais para quem vai executar." rows="3" />
            </label>
          </div>
          <label className="field-task-evidence-toggle">
            <input type="checkbox" checked={form.evidence} onChange={event => update('evidence', event.target.checked)} />
            <span><strong>Exigir comprovação no encerramento</strong><small>Solicita foto ou observação antes de concluir.</small></span>
          </label>
        </details>

        <div className="field-task-modal-footer">
          <div><FaShieldAlt /><span>{creationCopy.successNoun} registrada com autor, data e contexto da conta.</span></div>
          <button type="button" onClick={onClose} disabled={submitting}>Cancelar</button>
          <button type="submit" disabled={submitting}>{submitting ? 'Salvando com segurança…' : willReturnToMap ? 'Criar e voltar ao mapa' : creationCopy.submit} {!submitting && <FaArrowRight />}</button>
        </div>
      </form>
    </div>
  );
}

export function QuickExpenseModal({ properties, task = null, legacyTotal = 0, initialPropertyId = null, initialPlotId = null, onClose, onCreate, willReturnToMap = false }) {
  const initialProperty = properties.find(item => String(item.id) === String(initialPropertyId)) || properties[0];
  const initialPlot = initialProperty.plotRecords.find(item => plotMatchesLegacyId(item, initialPlotId));
  const receiptInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [preparingReceipt, setPreparingReceipt] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    property: String(initialProperty.id),
    plot: initialPlot ? String(initialPlot.id) : '',
    value: '',
    description: '',
    expenseDate: toLocalIsoDate(),
    category: 'outros',
    additionalToLegacy: false,
    submissionId: createClientRequestId(),
  });
  const selectedProperty = properties.find(item => String(item.id) === form.property) || initialProperty;

  useEffect(() => () => {
    if (receipt) releaseEvidencePreview(receipt);
  }, [receipt]);

  const update = (field, value) => {
    setError('');
    setForm(current => ({
      ...current,
      [field]: value,
      ...(field === 'property' ? { plot: '' } : {}),
    }));
  };

  const selectReceipt = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setPreparingReceipt(true);
    setError('');
    try {
      const requestedType = String(file.type || '').startsWith('image/') ? 'foto' : 'documento';
      const [prepared] = await prepareEvidenceFiles([file], requestedType);
      setReceipt(prepared);
    } catch (receiptError) {
      setError(receiptError?.message || 'Não foi possível preparar o comprovante.');
    } finally {
      setPreparingReceipt(false);
    }
  };

  const removeReceipt = () => setReceipt(null);

  const submit = async event => {
    event.preventDefault();
    if (submitting || preparingReceipt) return;

    setError('');
    try {
      parseCadernoExpenseValue(form.value);
      if (form.description.trim().length < 3) {
        throw new Error('Descreva a despesa com pelo menos 3 caracteres.');
      }
      if (legacyTotal > 0 && !form.additionalToLegacy) throw new Error('Confirme que o gasto não está incluído no custo anterior.');
      setSubmitting(true);
      await onCreate({ ...form, receipt });
    } catch (submitError) {
      setError(submitError?.message || 'Não foi possível registrar a despesa.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="field-task-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <form className="field-task-modal field-expense-modal" role="dialog" aria-modal="true" aria-labelledby="quick-expense-title" onSubmit={submit} onMouseDown={event => event.stopPropagation()}>
        <div className="field-task-modal-heading">
          <div>
            <span className="field-notebook-kicker">Registro financeiro simplificado</span>
            <h2 id="quick-expense-title">{task ? 'Registrar gasto da atividade' : 'Adicionar despesa rápida'}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar formulário"><FaTimes /></button>
        </div>

        <div className="field-expense-context">
          <span><FaDollarSign /></span>
          <div><strong>{task ? task.title : 'Gasto vinculado ao local correto'}</strong><small>{task ? `${task.property} · ${task.plot}` : 'A propriedade é obrigatória. Escolha o talhão somente quando a despesa for específica de uma área.'}</small></div>
        </div>

        <div className="field-task-form-grid">
          <label>
            <span>Propriedade *</span>
            <select value={form.property} onChange={event => update('property', event.target.value)} required disabled={Boolean(task)}>
              {properties.map(item => <option key={item.id} value={String(item.id)}>{item.name} · {PERMISSION_LABELS[item.permission] || 'Acesso permitido'}</option>)}
            </select>
          </label>
          <label>
            <span>Talhão <em className="field-expense-optional">opcional</em></span>
            <select value={form.plot} onChange={event => update('plot', event.target.value)} disabled={Boolean(task)}>
              <option value="">Despesa geral da propriedade</option>
              {selectedProperty.plotRecords.map(plot => <option value={String(plot.id)} key={plot.id}>{plot.name}</option>)}
            </select>
          </label>
          <label>
            <span>Valor *</span>
            <input value={form.value} onChange={event => update('value', event.target.value)} inputMode="decimal" placeholder="R$ 0,00" autoComplete="off" required />
          </label>
          <label>
            <span>Data da despesa *</span>
            <input type="date" value={form.expenseDate} onChange={event => update('expenseDate', event.target.value)} required />
          </label>
          {task && <label className="is-wide"><span>Categoria</span><select value={form.category} onChange={event => update('category', event.target.value)}>{Object.entries(FIELD_EXPENSE_CATEGORIES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>}
          <label className="is-wide">
            <span>Descrição *</span>
            <textarea value={form.description} onChange={event => update('description', event.target.value)} placeholder="Ex.: Compra de combustível para manutenção da propriedade" rows="3" maxLength="240" required />
            <small className="field-expense-character-count">{form.description.length}/240 caracteres</small>
          </label>
        </div>

        {legacyTotal > 0 && <label className="field-task-evidence-toggle">
          <input type="checkbox" checked={form.additionalToLegacy} onChange={event => update('additionalToLegacy', event.target.checked)} />
          <span><strong>Este gasto é adicional aos {formatFieldCost(legacyTotal)} já registrados</strong><small>Não está incluído no custo anterior de execução.</small></span>
        </label>}

        <div className="field-expense-receipt">
          <div>
            <span><FaReceipt /></span>
            <div><strong>Nota ou comprovante <em className="field-expense-optional">opcional</em></strong><small>Foto ou PDF, com compactação automática e armazenamento privado.</small></div>
          </div>
          <input ref={receiptInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,application/pdf,image/*" onChange={selectReceipt} />
          {!receipt ? (
            <button type="button" onClick={() => receiptInputRef.current?.click()} disabled={preparingReceipt}><FaPaperclip /> {preparingReceipt ? 'Preparando…' : 'Selecionar comprovante'}</button>
          ) : (
            <div className="field-expense-receipt-file">
              <FaFileAlt />
              <span><strong>{receipt.name}</strong><small>{receipt.sizeLabel}{receipt.originalSize > receipt.size ? ` · reduzido de ${receipt.originalSizeLabel}` : ''}</small></span>
              <button type="button" onClick={removeReceipt} aria-label="Remover comprovante"><FaTimes /></button>
            </div>
          )}
        </div>

        {error && <div className="field-expense-error" role="alert"><FaExclamationTriangle /><span>{error}</span></div>}

        <div className="field-task-modal-footer">
          <div><FaShieldAlt /><span>O registro ficará separado por conta, propriedade e usuário responsável.</span></div>
          <button type="button" onClick={onClose} disabled={submitting}>Cancelar</button>
          <button type="submit" disabled={submitting || preparingReceipt}><FaDollarSign /> {submitting ? 'Registrando…' : willReturnToMap ? 'Salvar e voltar ao mapa' : 'Registrar despesa'}</button>
        </div>
      </form>
    </div>
  );
}

function toLocalDateParts(value) {
  const date = value ? new Date(value) : new Date();
  const safeDate = Number.isNaN(date.getTime()) ? new Date() : date;
  return {
    date: `${safeDate.getFullYear()}-${String(safeDate.getMonth() + 1).padStart(2, '0')}-${String(safeDate.getDate()).padStart(2, '0')}`,
    time: `${String(safeDate.getHours()).padStart(2, '0')}:${String(safeDate.getMinutes()).padStart(2, '0')}`,
  };
}

export function EditTaskModal({ task, property, properties, onClose, onSave }) {
  const planned = toLocalDateParts(task.plannedAt);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState({
    property: String(property?.id || task.propertyId || ''),
    plot: String(property?.plotRecords?.find(plot => plotMatchesLegacyId(plot, task.plotId) || plot.name === task.plot)?.id || ''),
    type: task.type,
    title: task.title,
    description: task.description === 'Sem instruções adicionais.' ? '' : task.description,
    responsible: String(task.responsible.id || ''),
    dueDate: planned.date,
    dueTime: planned.time,
    priority: task.priority,
    plannedCost: task.plannedCost == null ? '' : String(task.plannedCost).replace('.', ','),
    evidence: task.evidence?.some(item => item !== 'Registro de conclusão'),
  });
  const selectedProperty = properties.find(item => String(item.id) === form.property);
  const update = (field, value) => setForm(current => {
    if (field !== 'property') return { ...current, [field]: value };
    const nextProperty = properties.find(item => String(item.id) === value);
    const responsible = nextProperty?.collaborators?.find(person => String(person.id) === current.responsible)
      || nextProperty?.collaborators?.[0];
    return { ...current, property: value, plot: '', responsible: String(responsible?.id || '') };
  });

  const submit = async event => {
    event.preventDefault();
    if (!form.title.trim() || !form.responsible || submitting) return;
    setSubmitting(true);
    setFormError('');
    try {
      await onSave(task.id, form);
    } catch (error) {
      setFormError(error?.message || 'Não foi possível salvar as alterações.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="field-task-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <form className="field-task-modal" role="dialog" aria-modal="true" aria-labelledby="edit-task-title" onSubmit={submit} onMouseDown={event => event.stopPropagation()}>
        <div className="field-task-modal-heading">
          <div><span className="field-notebook-kicker">Edição rastreável · {task.id}</span><h2 id="edit-task-title">Editar ordem de serviço</h2></div>
          <button type="button" onClick={onClose} aria-label="Fechar edição"><FaTimes /></button>
        </div>

        <div className="field-task-form-grid">
          <label><span>Propriedade *</span><select value={form.property} onChange={event => update('property', event.target.value)} required disabled={task.hasRecordedCosts}>{properties.map(item => <option key={item.id} value={String(item.id)}>{item.name}</option>)}</select></label>
          <label><span>Talhão <small>(opcional)</small></span><select value={form.plot} onChange={event => update('plot', event.target.value)} disabled={task.hasRecordedCosts}><option value="">Toda a propriedade</option>{(selectedProperty?.plotRecords || []).map(plot => <option key={plot.id} value={String(plot.id)}>{plot.name}</option>)}</select></label>
          <label><span>Tipo de atividade *</span><select value={form.type} onChange={event => update('type', event.target.value)}>{TASK_TYPES.map(type => <option key={type}>{type}</option>)}</select></label>
          <label><span>Responsável *</span><select value={form.responsible} onChange={event => update('responsible', event.target.value)} required>{(selectedProperty?.collaborators || []).map(person => <option value={String(person.id)} key={person.id}>{person.name} · {person.role}</option>)}</select></label>
          <label className="is-wide"><span>Título da atividade *</span><input value={form.title} onChange={event => update('title', event.target.value)} required /></label>
          <label className="is-wide"><span>Instruções para execução</span><textarea rows="3" value={form.description} onChange={event => update('description', event.target.value)} /></label>
          <label><span>Data prevista *</span><input type="date" value={form.dueDate} onChange={event => update('dueDate', event.target.value)} required /></label>
          <label><span>Horário</span><input type="time" value={form.dueTime} onChange={event => update('dueTime', event.target.value)} /></label>
          <label><span>Prioridade</span><select value={form.priority} onChange={event => update('priority', event.target.value)}><option value="normal">Normal</option><option value="high">Alta</option><option value="urgent">Urgente</option></select></label>
          <label><span>Custo previsto total (R$)</span><input value={form.plannedCost} onChange={event => update('plannedCost', event.target.value)} inputMode="decimal" placeholder="Não informado" /></label>
        </div>
        {task.hasRecordedCosts && <p className="field-task-modal-help">Local preservado porque a atividade já possui gastos registrados.</p>}

        <label className="field-task-evidence-toggle">
          <input type="checkbox" checked={form.evidence} onChange={event => update('evidence', event.target.checked)} />
          <span><strong>Exigir foto ou documento na conclusão</strong><small>A alteração ficará registrada no histórico da tarefa.</small></span>
        </label>
        {formError && <p className="field-modal-error"><FaExclamationTriangle /> {formError}</p>}

        <div className="field-task-modal-footer">
          <div><FaHistory /><span>Versão atual: {task.version || 1}. Alterações simultâneas serão bloqueadas.</span></div>
          <button type="button" onClick={onClose} disabled={submitting}>Cancelar</button>
          <button type="submit" disabled={submitting}>{submitting ? 'Salvando…' : 'Salvar alterações'} {!submitting && <FaCheck />}</button>
        </div>
      </form>
    </div>
  );
}

export function DeleteExpenseModal({ expense, onClose, onConfirm }) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const submit = async event => {
    event.preventDefault();
    if (pending) return;
    setPending(true);
    try { await onConfirm(expense); }
    catch (failure) { setError(failure.message || 'Não foi possível excluir o gasto.'); }
    finally { setPending(false); }
  };
  return <div className="field-task-modal-backdrop" onMouseDown={() => { if (!pending) onClose(); }}>
    <form className="field-task-modal" role="dialog" aria-modal="true" aria-labelledby="delete-expense-title" onSubmit={submit} onMouseDown={event => event.stopPropagation()}>
      <div className="field-task-modal-heading"><h2 id="delete-expense-title">Excluir gasto?</h2><button type="button" aria-label="Fechar exclusão" disabled={pending} onClick={onClose}><FaTimes /></button></div>
      <div className="field-expense-context"><FaReceipt /><div><strong>{expense.descricao}</strong><small>{formatFieldCost(expense.valor)}</small></div></div>
      <p>O gasto e seu comprovante serão excluídos definitivamente, sem estorno. O valor sairá do total realizado. Você poderá registrar outro gasto em seguida.</p>
      {error && <p role="alert" className="field-modal-error">{error}</p>}
      <div className="field-task-modal-footer"><button type="button" disabled={pending} onClick={onClose}>Cancelar</button><button type="submit" className="is-danger" disabled={pending}><FaTrashAlt /> {pending ? 'Excluindo...' : 'Confirmar exclusão'}</button></div>
    </form>
  </div>;
}

function CancelTaskModal({ task, onClose, onConfirm }) {
  const canRemoveQuickly = ['scheduled', 'late'].includes(task.status) && !task.execution && !(task.attachments?.length > 0);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const reasonLength = reason.trim().length;
  const missingReasonCharacters = Math.max(0, 10 - reasonLength);
  const validReason = canRemoveQuickly || reasonLength >= 10;

  const submit = async event => {
    event.preventDefault();
    if (!validReason || submitting) return;
    setSubmitting(true);
    setFormError('');
    try {
      await onConfirm(task.id, canRemoveQuickly ? '' : reason.trim());
    } catch (error) {
      setFormError(error?.message || 'Não foi possível excluir a tarefa.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="field-task-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <form className="field-task-modal field-cancel-modal" role="dialog" aria-modal="true" aria-labelledby="cancel-task-title" onSubmit={submit} onMouseDown={event => event.stopPropagation()}>
        <div className="field-task-modal-heading">
          <div><span className="field-notebook-kicker">Retirar da lista · {task.id}</span><h2 id="cancel-task-title">Retirar esta atividade?</h2></div>
          <button type="button" onClick={onClose} aria-label="Fechar exclusão"><FaTimes /></button>
        </div>
        <div className="field-cancel-warning"><FaShieldAlt /><div><strong>{canRemoveQuickly ? 'Ela sairá da sua lista ativa.' : 'Esta atividade já possui andamento.'}</strong><span>{canRemoveQuickly ? 'O cancelamento ficará apenas no histórico e não exige justificativa.' : 'Informe o motivo para preservar o contexto de quem participou da execução.'}</span></div></div>
        {!canRemoveQuickly && (
          <>
            <label className="field-approval-note">
              <span>Motivo da retirada *</span>
              <textarea
                rows="4"
                value={reason}
                onChange={event => setReason(event.target.value)}
                placeholder="Explique brevemente por que a atividade será retirada."
                aria-describedby="cancel-reason-help"
                aria-invalid={reasonLength > 0 && !validReason}
                autoFocus
              />
            </label>
            <small id="cancel-reason-help" className={`field-reason-counter ${validReason ? 'is-valid' : ''}`} aria-live="polite">
              {validReason ? 'Motivo suficiente.' : `Digite mais ${missingReasonCharacters} caractere${missingReasonCharacters === 1 ? '' : 's'}.`}
            </small>
          </>
        )}
        {formError && <p className="field-modal-error"><FaExclamationTriangle /> {formError}</p>}
        <div className="field-task-modal-footer">
          <div><FaHistory /><span>A atividade poderá ser consultada no filtro “Canceladas”.</span></div>
          <button type="button" onClick={onClose} disabled={submitting}>Manter atividade</button>
          <button type="submit" className="is-danger" disabled={!validReason || submitting}>{submitting ? 'Retirando…' : 'Retirar atividade'}</button>
        </div>
      </form>
    </div>
  );
}

export function DeleteTaskModal({ task, onClose, onConfirm }) {
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const submit = async event => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setFormError('');
    try {
      await onConfirm(task.id);
    } catch (error) {
      setFormError(error?.message || 'Não foi possível excluir a atividade.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="field-task-modal-backdrop" role="presentation" onMouseDown={() => { if (!submitting) onClose(); }}>
      <form className="field-task-modal field-cancel-modal" role="dialog" aria-modal="true" aria-labelledby="delete-task-title" onSubmit={submit} onMouseDown={event => event.stopPropagation()}>
        <div className="field-task-modal-heading">
          <div><span className="field-notebook-kicker">Exclusão definitiva · {task.id}</span><h2 id="delete-task-title">Excluir esta atividade para sempre?</h2></div>
          <button type="button" onClick={onClose} disabled={submitting} aria-label="Fechar exclusão"><FaTimes /></button>
        </div>
        <div className="field-cancel-warning"><FaExclamationTriangle /><div><strong>Esta ação não pode ser desfeita.</strong><span>A atividade, seus gastos, execuções, fotos, documentos e histórico serão apagados. Os valores também sairão dos totais.</span></div></div>
        {formError && <p className="field-modal-error"><FaExclamationTriangle /> {formError}</p>}
        <div className="field-task-modal-footer">
          <div><FaShieldAlt /><span>Disponível para usuários com permissão de editar e excluir nesta propriedade.</span></div>
          <button type="button" onClick={onClose} disabled={submitting}>Manter atividade</button>
          <button type="submit" className="is-danger" disabled={submitting}>{submitting ? 'Excluindo…' : 'Excluir definitivamente'}</button>
        </div>
      </form>
    </div>
  );
}

function TaskAttachmentModal({ task, onClose, onSubmit }) {
  const photoInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const evidenceRef = useRef([]);
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [processingFiles, setProcessingFiles] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fileError, setFileError] = useState('');
  evidenceRef.current = evidenceFiles;

  useEffect(() => () => evidenceRef.current.forEach(releaseEvidencePreview), []);

  const addFiles = async (event, type) => {
    const input = event.target;
    const files = Array.from(input.files || []);
    input.value = '';
    if (!files.length) return;
    setProcessingFiles(true);
    setFileError('');
    try {
      const prepared = await prepareEvidenceFiles(files, type);
      setEvidenceFiles(current => {
        const hashes = new Set(current.map(item => item.hashSha256));
        return [...current, ...prepared.filter(item => {
          if (hashes.has(item.hashSha256)) {
            releaseEvidencePreview(item);
            return false;
          }
          hashes.add(item.hashSha256);
          return true;
        })];
      });
    } catch (error) {
      setFileError(error?.message || 'Não foi possível compactar o arquivo.');
    } finally {
      setProcessingFiles(false);
    }
  };

  const removeFile = evidenceId => {
    setEvidenceFiles(current => {
      const removed = current.find(item => item.id === evidenceId);
      releaseEvidencePreview(removed);
      return current.filter(item => item.id !== evidenceId);
    });
  };

  const submit = async event => {
    event.preventDefault();
    if (!evidenceFiles.length || processingFiles || submitting) return;
    setSubmitting(true);
    setFileError('');
    try {
      await onSubmit(task.id, evidenceFiles);
    } catch (error) {
      setFileError(error?.message || 'Não foi possível anexar os arquivos.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="field-task-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <form className="field-task-modal field-attachment-modal" role="dialog" aria-modal="true" aria-labelledby="attachment-task-title" onSubmit={submit} onMouseDown={event => event.stopPropagation()}>
        <div className="field-task-modal-heading">
          <div><span className="field-notebook-kicker">Anexos da tarefa · {task.id}</span><h2 id="attachment-task-title">Adicionar fotos e documentos</h2></div>
          <button type="button" onClick={onClose} aria-label="Fechar anexos"><FaTimes /></button>
        </div>
        <div className="field-task-fixed-context"><FaMapMarkerAlt /><span><strong>{task.property} · {task.plot}</strong><small>Os arquivos serão vinculados somente a esta tarefa, propriedade e talhão.</small></span></div>
        <section className="field-execution-evidence">
          <div><span className="field-notebook-kicker">Compactação antes do envio</span><h3>Fotos em WebP; TXT/CSV em GZIP; PDF, DOCX e XLSX preservam a compactação nativa.</h3></div>
          <div className="field-execution-evidence-grid">
            <input ref={photoInputRef} className="field-evidence-file-input" type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" multiple onChange={event => addFiles(event, 'foto')} />
            <input ref={documentInputRef} className="field-evidence-file-input" type="file" accept=".pdf,.txt,.csv,.docx,.xlsx" multiple onChange={event => addFiles(event, 'documento')} />
            <button type="button" onClick={() => photoInputRef.current?.click()} disabled={processingFiles || submitting}><FaCamera /><span><strong>Fotografar ou escolher</strong><small>Redimensionamento máximo de 1920 px</small></span></button>
            <button type="button" onClick={() => documentInputRef.current?.click()} disabled={processingFiles || submitting}><FaFileAlt /><span><strong>Escolher documento</strong><small>PDF, TXT, CSV, DOCX ou XLSX</small></span></button>
          </div>
          {processingFiles && <p className="is-processing"><FaShieldAlt /> Compactando e calculando a assinatura digital…</p>}
          {evidenceFiles.length > 0 && <div className="field-evidence-file-list">{evidenceFiles.map(evidence => (
            <div key={evidence.id} className="field-evidence-file-item">
              {evidence.previewUrl ? <img src={evidence.previewUrl} alt="Prévia do anexo" /> : <span><FaFileAlt /></span>}
              <div><strong>{evidence.name}</strong><small>{evidence.originalSize > evidence.size ? `${evidence.originalSizeLabel} → ${evidence.sizeLabel}` : evidence.sizeLabel} · SHA-256 {evidence.hashSha256.slice(0, 12)}…</small></div>
              <button type="button" onClick={() => removeFile(evidence.id)} disabled={submitting} aria-label={`Remover ${evidence.name}`}><FaTimes /></button>
            </div>
          ))}</div>}
          {fileError && <p className="is-error"><FaExclamationTriangle /> {fileError}</p>}
        </section>
        <div className="field-task-modal-footer">
          <div><FaShieldAlt /><span>Arquivos privados, assinados e sem sobrescrita.</span></div>
          <button type="button" onClick={onClose} disabled={submitting}>Cancelar</button>
          <button type="submit" disabled={!evidenceFiles.length || processingFiles || submitting}>{submitting ? 'Enviando…' : `Anexar ${evidenceFiles.length || ''} arquivo(s)`} {!submitting && <FaPaperclip />}</button>
        </div>
      </form>
    </div>
  );
}

function ExecutionModal({ task, accountLabel, onClose, onSubmit }) {
  const isIrrigation = task.type === 'Irrigação';
  const submissionIdRef = useRef(createClientRequestId());
  const photoInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const evidenceRef = useRef([]);
  const [processingFiles, setProcessingFiles] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fileError, setFileError] = useState('');
  const [locationStatus, setLocationStatus] = useState('idle');
  const [form, setForm] = useState({
    summary: isIrrigation
      ? 'Irrigação executada conforme a lâmina recomendada para o talhão.'
      : 'Vistoria realizada nos pontos indicados pela ordem de serviço.',
    notes: '',
    actualDate: toLocalIsoDate(),
    startTime: '08:40',
    endTime: '10:15',
    cost: '',
    appliedAmount: isIrrigation ? '12,6' : '',
    finalReading: '',
    evidenceFiles: [],
    location: null,
    submissionId: submissionIdRef.current,
  });

  evidenceRef.current = form.evidenceFiles;

  useEffect(() => () => {
    evidenceRef.current.forEach(releaseEvidencePreview);
  }, []);

  const update = (field, value) => setForm(current => ({ ...current, [field]: value }));
  const photoCount = form.evidenceFiles.filter(item => item.type === 'foto').length;
  const documentCount = form.evidenceFiles.filter(item => item.type === 'documento').length;
  const hasEvidence = form.evidenceFiles.length > 0;

  const addEvidenceFiles = async (event, type) => {
    const input = event.target;
    const files = Array.from(input.files || []);
    input.value = '';
    if (!files.length) return;

    setProcessingFiles(true);
    setFileError('');
    try {
      const prepared = await prepareEvidenceFiles(files, type);
      setForm(current => {
        const knownHashes = new Set(current.evidenceFiles.map(item => item.hashSha256));
        const uniqueFiles = prepared.filter(item => {
          if (knownHashes.has(item.hashSha256)) {
            releaseEvidencePreview(item);
            return false;
          }
          knownHashes.add(item.hashSha256);
          return true;
        });
        return { ...current, evidenceFiles: [...current.evidenceFiles, ...uniqueFiles] };
      });
    } catch (error) {
      setFileError(error?.message || 'Não foi possível preparar o arquivo selecionado.');
    } finally {
      setProcessingFiles(false);
    }
  };

  const removeEvidence = (evidenceId) => {
    setForm(current => {
      const removed = current.evidenceFiles.find(item => item.id === evidenceId);
      releaseEvidencePreview(removed);
      return {
        ...current,
        evidenceFiles: current.evidenceFiles.filter(item => item.id !== evidenceId),
      };
    });
  };

  const confirmLocation = () => {
    if (!navigator.geolocation) {
      setLocationStatus('unsupported');
      setFileError('Este dispositivo não disponibiliza localização para o navegador.');
      return;
    }

    setLocationStatus('loading');
    setFileError('');
    navigator.geolocation.getCurrentPosition(
      position => {
        update('location', {
          latitude: Number(position.coords.latitude.toFixed(6)),
          longitude: Number(position.coords.longitude.toFixed(6)),
          accuracy: Number(position.coords.accuracy.toFixed(2)),
          capturedAt: new Date(position.timestamp).toISOString(),
        });
        setLocationStatus('confirmed');
      },
      error => {
        setLocationStatus('error');
        setFileError(error.code === error.PERMISSION_DENIED
          ? 'A permissão de localização foi negada. Você ainda pode enviar as evidências sem geolocalização.'
          : 'Não foi possível obter a localização. Verifique o sinal e tente novamente.');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.summary.trim() || !hasEvidence || processingFiles || submitting) return;
    setSubmitting(true);
    setFileError('');
    try {
      await onSubmit(task.id, form);
    } catch (error) {
      setFileError(error?.message || 'Não foi possível registrar a execução.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="field-task-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <form className="field-task-modal field-execution-modal" role="dialog" aria-modal="true" aria-labelledby="execution-modal-title" onSubmit={submit} onMouseDown={event => event.stopPropagation()}>
        <div className="field-task-modal-heading">
          <div>
            <span className="field-notebook-kicker">Execução em campo · {task.id}</span>
            <h2 id="execution-modal-title">Registrar conclusão da atividade</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar execução"><FaTimes /></button>
        </div>

        <div className="field-execution-context">
          <div><span>Conta</span><strong>{accountLabel}</strong></div><FaChevronRight />
          <div><span>Propriedade</span><strong>{task.property}</strong></div><FaChevronRight />
          <div><span>Área de execução</span><strong>{task.plot}</strong></div>
        </div>

        <div className="field-execution-assignment">
          <span className="field-task-avatar is-responsible">{task.responsible.initials}</span>
          <div><small>Executando como</small><strong>{task.responsible.name}</strong><em>{task.responsible.role}</em></div>
          <span><FaShieldAlt /> Identidade confirmada</span>
        </div>

        <div className="field-task-form-grid">
          <label>
            <span>Data da execução *</span>
            <input type="date" value={form.actualDate} onChange={event => update('actualDate', event.target.value)} required />
          </label>
          <div className="field-execution-time-grid">
            <label><span>Início</span><input type="time" value={form.startTime} onChange={event => update('startTime', event.target.value)} /></label>
            <label><span>Término</span><input type="time" value={form.endTime} onChange={event => update('endTime', event.target.value)} /></label>
          </div>
          <label className="is-wide">
            <span>O que foi executado? *</span>
            <textarea value={form.summary} onChange={event => update('summary', event.target.value)} rows="3" placeholder="Descreva o serviço realizado e o resultado encontrado." required />
          </label>
          {isIrrigation && (
            <>
              <label><span>Lâmina aplicada (mm)</span><input value={form.appliedAmount} onChange={event => update('appliedAmount', event.target.value)} placeholder="Ex.: 12,6" /></label>
              <label><span>Leitura final do hidrômetro</span><input value={form.finalReading} onChange={event => update('finalReading', event.target.value)} placeholder="Ex.: 1842,5 m³" /></label>
            </>
          )}
          <label className={isIrrigation ? '' : 'is-wide'}><span>Observações adicionais</span><input value={form.notes} onChange={event => update('notes', event.target.value)} placeholder="Ocorrências, materiais ou ressalvas" /></label>
        </div>

        <section className="field-execution-evidence">
          <div>
            <span className="field-notebook-kicker">Evidências obrigatórias</span>
            <h3>Comprove a execução antes de enviar</h3>
          </div>
          <div className="field-execution-evidence-grid">
            <input
              ref={photoInputRef}
              className="field-evidence-file-input"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              multiple
              onChange={event => addEvidenceFiles(event, 'foto')}
            />
            <input
              ref={documentInputRef}
              className="field-evidence-file-input"
              type="file"
              accept=".pdf,.txt,.csv,.docx,.xlsx"
              multiple
              onChange={event => addEvidenceFiles(event, 'documento')}
            />
            <button type="button" className={photoCount > 0 ? 'is-added' : ''} onClick={() => photoInputRef.current?.click()} disabled={processingFiles || submitting}>
              <FaCamera /><span><strong>{photoCount > 0 ? `${photoCount} foto(s) preparada(s)` : 'Fotografar ou escolher foto'}</strong><small>JPEG, PNG, WebP, HEIC · até 25 MB</small></span>{photoCount > 0 && <FaCheckCircle />}
            </button>
            <button type="button" className={documentCount > 0 ? 'is-added' : ''} onClick={() => documentInputRef.current?.click()} disabled={processingFiles || submitting}>
              <FaFileAlt /><span><strong>{documentCount > 0 ? `${documentCount} documento(s) preparado(s)` : 'Escolher documento'}</strong><small>PDF, planilha ou relatório · até 25 MB</small></span>{documentCount > 0 && <FaCheckCircle />}
            </button>
          </div>
          {processingFiles && <p className="is-processing"><FaShieldAlt /> Verificando formato e calculando a assinatura digital dos arquivos…</p>}
          {!hasEvidence && !processingFiles && <p><FaExclamationTriangle /> Adicione ao menos uma foto ou documento real para enviar a execução.</p>}
          {form.evidenceFiles.length > 0 && (
            <div className="field-evidence-file-list" aria-label="Evidências preparadas">
              {form.evidenceFiles.map(evidence => (
                <div key={evidence.id} className="field-evidence-file-item">
                  {evidence.previewUrl
                    ? <img src={evidence.previewUrl} alt="Prévia da evidência" />
                    : <span><FaFileAlt /></span>}
                  <div>
                    <strong>{evidence.name}</strong>
                    <small>{evidence.sizeLabel} · SHA-256 {evidence.hashSha256.slice(0, 12)}…</small>
                  </div>
                  <button type="button" onClick={() => removeEvidence(evidence.id)} disabled={submitting} aria-label={`Remover ${evidence.name}`}><FaTimes /></button>
                </div>
              ))}
            </div>
          )}
          {fileError && <p className="is-error"><FaExclamationTriangle /> {fileError}</p>}
          <button type="button" className={`field-execution-location is-${locationStatus}`} onClick={confirmLocation} disabled={locationStatus === 'loading' || submitting}>
            <FaMapMarkerAlt />
            <span>
              <strong>{locationStatus === 'confirmed' ? 'Localização confirmada' : locationStatus === 'loading' ? 'Obtendo localização…' : 'Confirmar localização da execução'}</strong>
              <small>{form.location ? `Precisão aproximada de ${Math.round(form.location.accuracy)} m` : 'Opcional · solicitada somente após seu toque'}</small>
            </span>
            {locationStatus === 'confirmed' && <FaCheckCircle />}
          </button>
        </section>

        <div className="field-task-modal-footer">
          <div><FaShieldAlt /><span>O envio gera um registro datado e encaminha a atividade para aprovação.</span></div>
          <button type="button" onClick={onClose} disabled={submitting}>Cancelar</button>
          <button type="submit" disabled={!hasEvidence || processingFiles || submitting}>{submitting ? 'Registrando evidências…' : 'Enviar para aprovação'} {!submitting && <FaArrowRight />}</button>
        </div>
      </form>
    </div>
  );
}

function ApprovalModal({ task, onClose, onApprove, onReturn }) {
  const [reviewNote, setReviewNote] = useState('Execução conferida e evidências compatíveis com a ordem de serviço.');
  const [submittingAction, setSubmittingAction] = useState('');
  const [reviewError, setReviewError] = useState('');
  const execution = task.execution || {};

  const submitReview = async (approved) => {
    if (!reviewNote.trim() || submittingAction) return;
    setSubmittingAction(approved ? 'approve' : 'return');
    setReviewError('');
    try {
      if (approved) await onApprove(task.id, reviewNote);
      else await onReturn(task.id, reviewNote);
    } catch (error) {
      setReviewError(error?.message || 'Não foi possível registrar o parecer.');
    } finally {
      setSubmittingAction('');
    }
  };

  return (
    <div className="field-task-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="field-task-modal field-approval-modal" role="dialog" aria-modal="true" aria-labelledby="approval-modal-title" onMouseDown={event => event.stopPropagation()}>
        <div className="field-task-modal-heading">
          <div>
            <span className="field-notebook-kicker">Revisão gerencial · {task.id}</span>
            <h2 id="approval-modal-title">Aprovar registro de execução</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar aprovação"><FaTimes /></button>
        </div>

        <div className="field-approval-title">
          <span className="field-task-avatar is-responsible">{task.responsible.initials}</span>
          <div><small>Enviado por {task.responsible.name}</small><strong>{task.title}</strong><span>{task.property} <FaChevronRight /> {task.plot}</span></div>
        </div>

        <div className="field-approval-summary">
          <span className="field-notebook-kicker">Relato da execução</span>
          <p>{execution.summary}</p>
          {execution.notes && <blockquote>{execution.notes}</blockquote>}
          <div>
            <span><FaClock /><strong>Período executado</strong><small>{execution.actualPeriod}</small></span>
            <span><FaCamera /><strong>Evidências</strong><small>{execution.photos || 0} foto(s) · {execution.document ? 'documento anexado' : 'sem documento'}</small></span>
            <span><FaMapMarkerAlt /><strong>Localização</strong><small>{execution.locationConfirmed ? `${task.property} · ${task.plot}` : 'Não confirmada'}</small></span>
            <span><FaFileAlt /><strong>Gasto realizado</strong><small>{task.costSummary?.realizedTotal == null ? 'Não informado' : formatFieldCost(task.costSummary.realizedTotal)}</small></span>
          </div>
        </div>

        <div className="field-approval-evidence-preview">
          {Array.from({ length: Math.min(execution.photos || 0, 3) }, (_, index) => (
            <div key={index}><FaSeedling /><span>Foto de campo {index + 1}</span><small>Talhão confirmado</small></div>
          ))}
          {execution.document && <div><FaFileAlt /><span>Documento da execução</span><small>Anexo protegido</small></div>}
        </div>

        <label className="field-approval-note">
          <span>Parecer do gestor</span>
          <textarea rows="3" value={reviewNote} onChange={event => setReviewNote(event.target.value)} placeholder="Informe o motivo da aprovação ou da devolução." />
        </label>
        {reviewError && <p className="field-approval-error"><FaExclamationTriangle /> {reviewError}</p>}

        <div className="field-approval-audit-note">
          <FaShieldAlt /><div><strong>Registro auditável</strong><span>A aprovação preserva executor, gestor, data, evidências e vínculo com o talhão. Correções futuras gerarão uma nova versão.</span></div>
        </div>

        <div className="field-approval-actions">
          <button type="button" onClick={() => submitReview(false)} disabled={Boolean(submittingAction)}>{submittingAction === 'return' ? 'Registrando parecer…' : 'Devolver para correção'}</button>
          <button type="button" onClick={() => submitReview(true)} disabled={Boolean(submittingAction)}><FaCheckCircle /> {submittingAction === 'approve' ? 'Registrando aprovação…' : 'Aprovar e registrar no histórico'}</button>
        </div>
      </section>
    </div>
  );
}

export default function FieldNotebookPreview({
  darkMode,
  setDarkMode,
  scenario,
  onNavigate,
  evaluationMode = false,
  sourceAlertId = null,
  focusOrderId = null,
  initialAction = null,
  initialPropertyId = null,
  initialPlotId = null,
  returnTarget = null,
  returnContext = null,
  accountLabel = 'Conta de demonstração',
  currentUserId = null,
  currentUserName = 'Usuário de demonstração',
  realProperties = [],
  realContextLoading = false,
  realContextError = '',
  pecuariaEnabled = false,
}) {
  const operationActive = scenario === 'operacao';
  const sourceAlert = sourceAlertId || new URLSearchParams(window.location.search).get('fromAlert');
  const availableProperties = useMemo(() => {
    const source = evaluationMode ? propertiesForModule(realProperties, 'caderno') : PROPERTIES;
    return (source || []).map(property => {
      const plotRecords = (property.plots || [])
        .map(plot => typeof plot === 'string' ? { id: plot, name: plot } : plot)
        .filter(plot => plot?.name);

      return {
        ...property,
        plotRecords,
        plots: plotRecords.map(plot => plot.name),
        collaborators: (property.collaborators?.length
          ? property.collaborators
          : (evaluationMode ? [] : RESPONSIBLES)
        ).map(person => ({
          ...person,
          id: String(person.id),
          initials: person.initials || initials(person.name),
        })),
      };
    });
  }, [evaluationMode, realProperties]);
  const editableProperties = useMemo(
    () => availableProperties.filter(property => (
      propertyActionAllowed(property, 'caderno', 'create', property.permission !== 'viewer')
      && property.collaborators.length > 0
    )),
    [availableProperties]
  );
  const expenseProperties = useMemo(
    () => availableProperties.filter(property => propertyActionAllowed(property, 'caderno', 'create', property.permission !== 'viewer')),
    [availableProperties]
  );
  const usingRealCaderno = evaluationMode && cadernoRuntime.readsEnabled;
  const { podeUsar, loading: planLoading } = usePlanLimits(currentUserId);
  const realPlotCount = availableProperties.reduce((total, property) => total + property.plots.length, 0);
  const [tasks, setTasks] = useState(operationActive && !usingRealCaderno ? BASE_TASKS : []);
  const [expenses, setExpenses] = useState([]);
  const [expensesError, setExpensesError] = useState('');
  const [cleanupError, setCleanupError] = useState('');
  const [filter, setFilter] = useState('all');
  const [propertyFilter, setPropertyFilter] = useState('all');
  const [showPecuariaHistory, setShowPecuariaHistory] = useState(false);
  const [plotFilter, setPlotFilter] = useState('all');
  const plotFilterOptions = useMemo(() => availableProperties
    .filter(property => propertyFilter === 'all' || property.name === propertyFilter)
    .flatMap(property => property.plotRecords.map(plot => ({
      value: `${encodeURIComponent(String(property.id ?? property.name))}::${encodeURIComponent(String(plot.id ?? plot.name))}`,
      propertyId: property.id,
      propertyName: property.name,
      plotId: plot.id,
      physicalPlotId: plot._physicalPlotId || null,
      plotName: plot.name,
      legacyPlotIds: plot._legacyPlotIds || [plot.id],
    }))), [availableProperties, propertyFilter]);
  const [selectedTaskId, setSelectedTaskId] = useState(operationActive && !cadernoRuntime.readsEnabled ? BASE_TASKS[0].id : null);
  const [showCreate, setShowCreate] = useState(Boolean(sourceAlert) && !evaluationMode);
  const [creationMode, setCreationMode] = useState('order');
  const [showExpense, setShowExpense] = useState(false);
  const [expenseTaskId, setExpenseTaskId] = useState(null);
  const [deletingExpense, setDeletingExpense] = useState(null);
  const launchContextAppliedRef = useRef(false);
  const taskDetailRef = useRef(null);
  const [executionTaskId, setExecutionTaskId] = useState(null);
  const [approvalTaskId, setApprovalTaskId] = useState(null);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [cancellingTaskId, setCancellingTaskId] = useState(null);
  const [deletingTaskId, setDeletingTaskId] = useState(null);
  const [attachmentTaskId, setAttachmentTaskId] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [realOrdersLoading, setRealOrdersLoading] = useState(false);
  const [realOrdersError, setRealOrdersError] = useState('');
  const [legacyEventsError, setLegacyEventsError] = useState('');
  const [generatingReport, setGeneratingReport] = useState(false);
  const loadRealOrders = useCallback(async () => {
    if (!usingRealCaderno) return;

    setRealOrdersLoading(true);
    setRealOrdersError('');
    setLegacyEventsError('');
    setExpensesError('');
    try {
      if (cadernoRuntime.writesEnabled) {
        try {
          await cleanupDeletedCadernoFiles();
          setCleanupError('');
        } catch (error) {
          setCleanupError(error.message || 'Falha no armazenamento.');
        }
      }
      const plotIds = availableProperties.flatMap(property => property.plotRecords.flatMap(
        plot => plot._legacyPlotIds || [plot.id]
      ));
      const [ordersResult, legacyEventsResult, expensesResult] = await Promise.allSettled([
        listCadernoOrders(),
        listLegacyCadernoEvents(plotIds),
        listCadernoExpenses(),
      ]);
      if (ordersResult.status === 'rejected') throw ordersResult.reason;

      // Nunca renderizar ordens de propriedades fora da lista própria/compartilhada do usuário,
      // mesmo que a consulta retorne registros adicionais.
      const ownPropertyIds = new Set(availableProperties.map(property => String(property.id)));
      const mappedOrders = ordersResult.value
        .filter(order => ownPropertyIds.has(String(order.propriedade_id)))
        .map(order => mapDatabaseOrderToTask(
          order,
          availableProperties,
          currentUserId,
          currentUserName
        ));
      const persistedLegacyEvents = legacyEventsResult.status === 'fulfilled' ? legacyEventsResult.value : [];
      if (legacyEventsResult.status === 'rejected') {
        setLegacyEventsError(legacyEventsResult.reason?.message || 'Não foi possível carregar os eventos históricos.');
      }
      const offlineLegacyEvents = getQueue()
        .filter(item => item.status === 'pending' && item.type === 'event' && item.eventData)
        .map(item => ({ ...item.eventData, id: item.id, created_at: item.createdAt, _offline: true }));
      const mappedLegacyEvents = [...offlineLegacyEvents, ...persistedLegacyEvents]
        .map(event => mapLegacyEventToTask(event, availableProperties, currentUserId, currentUserName))
        .filter(Boolean);
      const mappedTasks = [...mappedOrders, ...mappedLegacyEvents].sort((left, right) => {
        const leftDate = new Date(left.plannedAt || 0).getTime() || 0;
        const rightDate = new Date(right.plannedAt || 0).getTime() || 0;
        return rightDate - leftDate;
      });
      setTasks(mappedTasks);
      setExpenses(expensesResult.status === 'fulfilled' ? expensesResult.value : []);
      if (expensesResult.status === 'rejected') setExpensesError(expensesResult.reason?.message || 'Falha ao carregar despesas.');
      setSelectedTaskId(current => mappedTasks.some(task => task.id === current) ? current : (mappedTasks[0]?.id || null));
    } catch (error) {
      setTasks([]);
      setExpenses([]);
      setSelectedTaskId(null);
      setRealOrdersError(error?.message || 'Não foi possível carregar as ordens reais.');
    } finally {
      setRealOrdersLoading(false);
    }
  }, [availableProperties, currentUserId, currentUserName, usingRealCaderno]);

  useEffect(() => {
    if (usingRealCaderno || !evaluationMode || !operationActive || availableProperties.length === 0) return;
    const contextualTasks = contextualizeDemoTasks(availableProperties, currentUserId, currentUserName);
    setTasks(contextualTasks);
    setSelectedTaskId(contextualTasks[0]?.id || null);
    setPropertyFilter('all');
  }, [evaluationMode, operationActive, availableProperties, currentUserId, currentUserName, usingRealCaderno]);

  useEffect(() => {
    if (!evaluationMode || !operationActive || !cadernoRuntime.readsEnabled || realContextLoading || realContextError) return;
    loadRealOrders();
  }, [evaluationMode, operationActive, realContextLoading, realContextError, loadRealOrders]);

  useEffect(() => {
    if (!usingRealCaderno) return undefined;
    const refreshOfflineEvents = () => loadRealOrders();
    window.addEventListener(QUEUE_UPDATED_EVENT, refreshOfflineEvents);
    return () => window.removeEventListener(QUEUE_UPDATED_EVENT, refreshOfflineEvents);
  }, [loadRealOrders, usingRealCaderno]);

  useEffect(() => {
    if (evaluationMode && sourceAlert && editableProperties.length > 0) {
      setShowCreate(true);
    }
  }, [evaluationMode, sourceAlert, editableProperties.length]);

  useEffect(() => {
    if (!focusOrderId) return;
    const focusedTask = tasks.find(task => (
      String(task.id) === String(focusOrderId)
      || String(task.recordId) === String(focusOrderId)
    ));
    if (!focusedTask) return;
    setSelectedTaskId(focusedTask.id);
    setPropertyFilter('all');
    setPlotFilter('all');
    setFilter(focusedTask.status === 'cancelled' ? 'cancelled' : 'all');
    if (window.matchMedia('(max-width: 900px)').matches) {
      window.setTimeout(() => taskDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
    }
  }, [focusOrderId, tasks]);

  const selectTask = useCallback(task => {
    setSelectedTaskId(task.id);
    if (window.matchMedia('(max-width: 900px)').matches) {
      window.setTimeout(() => {
        taskDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        taskDetailRef.current?.focus({ preventScroll: true });
      }, 0);
    }
  }, []);

  const clearAlertContext = () => {
    if (!sourceAlert) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('fromAlert');
    window.history.replaceState({}, '', url);
  };

  const closeCreate = () => {
    setShowCreate(false);
    clearAlertContext();
    if (initialAction === 'new-order') {
      returnToOrigin('', 'order-cancelled');
    }
  };

  const openCreate = (mode = 'order') => {
    const requestedMode = mode === 'task' ? 'task' : 'order';
    if (usingRealCaderno && !cadernoRuntime.writesEnabled) {
      setFeedback('No momento só é possível consultar as ordens. A criação de novas atividades está temporariamente desativada.');
      return;
    }
    if (evaluationMode && realContextLoading) {
      setFeedback('Aguarde enquanto as propriedades e os talhões da conta são carregados.');
      return;
    }
    if (evaluationMode && realContextError) {
      setFeedback('Não foi possível carregar o contexto da conta. Tente retornar ao painel e abrir o Caderno novamente.');
      return;
    }
    if (evaluationMode && availableProperties.length === 0) {
      setFeedback(`Cadastre uma propriedade antes de criar ${requestedMode === 'task' ? 'uma tarefa' : 'uma ordem de serviço'}. O talhão é opcional.`);
      return;
    }
    if (evaluationMode && editableProperties.length === 0) {
      setFeedback('As propriedades disponíveis estão em modo de visualização. Solicite acesso de editor para criar atividades.');
      return;
    }
    setCreationMode(requestedMode);
    setShowCreate(true);
  };

  const openExpense = () => {
    if (usingRealCaderno && !cadernoRuntime.writesEnabled) {
      setFeedback('No momento só é possível consultar o Caderno. O registro de despesas está temporariamente desativado.');
      return;
    }
    if (evaluationMode && realContextLoading) {
      setFeedback('Aguarde enquanto as propriedades da conta são carregadas.');
      return;
    }
    if (evaluationMode && realContextError) {
      setFeedback('Não foi possível carregar as propriedades autorizadas desta conta.');
      return;
    }
    if (evaluationMode && expenseProperties.length === 0) {
      setFeedback('É necessário ter acesso de proprietário ou editor a uma propriedade para registrar despesas.');
      return;
    }
    setExpenseTaskId(null);
    setShowExpense(true);
    return true;
  };

  useEffect(() => {
    if (launchContextAppliedRef.current || !initialAction || realContextLoading) return;
    launchContextAppliedRef.current = true;

    const initialProperty = availableProperties.find(item => String(item.id) === String(initialPropertyId));
    if (initialProperty) {
      setPropertyFilter(initialProperty.name);
      const initialPlot = initialProperty.plotRecords.find(item => plotMatchesLegacyId(item, initialPlotId));
      if (initialPlot) {
        setPlotFilter(`${encodeURIComponent(String(initialProperty.id ?? initialProperty.name))}::${encodeURIComponent(String(initialPlot.id ?? initialPlot.name))}`);
      }
    }

    if (initialAction === 'new-order') openCreate('order');
    if (initialAction === 'new-expense') openExpense();
  }, [availableProperties, initialAction, initialPlotId, initialPropertyId, realContextLoading]);

  const returnToOrigin = useCallback((notice, returnedFrom = 'caderno') => {
    if (!returnTarget || typeof onNavigate !== 'function') return false;
    onNavigate(returnTarget, {
      ...(returnContext || {}),
      notice,
      returnedFrom,
    });
    return true;
  }, [onNavigate, returnContext, returnTarget]);

  const selectedTask = tasks.find(task => task.id === selectedTaskId) || null;
  const executionTask = tasks.find(task => task.id === executionTaskId) || null;
  const approvalTaskRecord = tasks.find(task => task.id === approvalTaskId) || null;
  const approvalTask = approvalTaskRecord ? { ...approvalTaskRecord, costSummary: expensesError ? null : summarizeTaskCosts(approvalTaskRecord, expenses) } : null;
  const editingTaskRecord = tasks.find(task => task.id === editingTaskId) || null;
  const editingTask = editingTaskRecord ? {
    ...editingTaskRecord,
    hasRecordedCosts: summarizeTaskCosts(editingTaskRecord, expenses).expenses.length > 0
      || editingTaskRecord.costExecutions?.some(execution => Number(execution.custo_real) > 0),
  } : null;
  const expenseTask = tasks.find(task => task.id === expenseTaskId) || null;
  const cancellingTask = tasks.find(task => task.id === cancellingTaskId) || null;
  const deletingTask = tasks.find(task => task.id === deletingTaskId) || null;
  const attachmentTask = tasks.find(task => task.id === attachmentTaskId) || null;
  const editingProperty = editingTask
    ? availableProperties.find(property => (
        String(property.id) === String(editingTask.propertyId)
        || property.name === editingTask.property
      ))
    : null;
  const counts = summarizeFieldTasks(tasks.filter(task => !task.isLegacyEvent));

  const visibleTasks = useMemo(() => tasks.filter(task => {
    const matchesProperty = propertyFilter === 'all' || task.property === propertyFilter;
    if (!matchesProperty) return false;
    const selectedPlot = plotFilterOptions.find(plot => plot.value === plotFilter);
    const matchesPlot = plotFilter === 'all' || Boolean(selectedPlot && (
      (
        task.propertyId != null
        && task.plotId != null
        && String(task.propertyId) === String(selectedPlot.propertyId)
        && selectedPlot.legacyPlotIds.some(id => String(task.plotId) === String(id))
      )
      || (task.property === selectedPlot.propertyName && task.plot === selectedPlot.plotName)
    ));
    if (!matchesPlot) return false;
    if (filter === 'all') return task.status !== 'cancelled';
    const operationalBucket = classifyFieldTask(task);
    if (filter === 'today') return operationalBucket === 'today';
    if (filter === 'late') return operationalBucket === 'late';
    if (filter === 'approval') return operationalBucket === 'approval';
    if (filter === 'completed') return operationalBucket === 'completed';
    return operationalBucket === 'cancelled';
  }), [tasks, filter, propertyFilter, plotFilter, plotFilterOptions]);

  const visibleExpenses = useMemo(() => {
    const selectedPlot = plotFilterOptions.find(plot => plot.value === plotFilter);
    return expenses.filter(expense => {
      const property = availableProperties.find(item => String(item.id) === String(expense.propriedade_id));
      if (!property) return false;
      if (propertyFilter !== 'all' && property?.name !== propertyFilter) return false;
      if (plotFilter !== 'all' && (!selectedPlot
        || String(expense.propriedade_id) !== String(selectedPlot.propertyId)
        || !selectedPlot.legacyPlotIds.some(id => String(expense.talhao_id) === String(id)))) return false;
      return true;
    });
  }, [availableProperties, expenses, plotFilter, plotFilterOptions, propertyFilter]);

  const exportFieldNotebookReport = async () => {
    if (!planLoading && !podeUsar('relatorios_pdf')) {
      setFeedback('A exportação de relatórios PDF está disponível nos planos com relatórios avançados.');
      return;
    }
    if (visibleTasks.length === 0 && visibleExpenses.length === 0) {
      setFeedback('Não há registros nos filtros selecionados para exportar.');
      return;
    }
    if (expensesError || legacyEventsError || realOrdersError) {
      setFeedback('Atualize o caderno antes de exportar: há registros ou gastos que não puderam ser carregados.');
      return;
    }

    setGeneratingReport(true);
    try {
      const selectedPlot = plotFilterOptions.find(plot => plot.value === plotFilter);
      const propertyLabel = propertyFilter === 'all' ? 'Todas as propriedades' : propertyFilter;
      const plotLabel = selectedPlot?.plotName || 'Todos os talhões';
      const statusLabel = FILTERS.find(item => item.key === filter)?.label || 'Todas';
      const filteredCounts = visibleTasks.reduce((summary, task) => {
        summary[task.status] = (summary[task.status] || 0) + 1;
        return summary;
      }, {});
      const financialEntries = collectRealizedFieldCosts({
        expenses: visibleExpenses,
        executions: tasks.flatMap(task => task.costExecutions || []),
        events: tasks.filter(task => task.isLegacyEvent && !task.isOfflineLegacyEvent).map(task => ({
          id: task.recordId, custo_total: task.legacyCost, propriedade_id: task.propertyId, talhao_id: task.plotId, data_evento: task.plannedAt,
        })),
      }).filter(entry => {
        const property = availableProperties.find(item => String(item.id) === String(entry.propertyId));
        return property && (propertyFilter === 'all' || property.name === propertyFilter)
          && (plotFilter === 'all' || (String(entry.propertyId) === String(selectedPlot?.propertyId)
            && selectedPlot?.legacyPlotIds.some(id => String(id) === String(entry.plotId))));
      });
      const totalExpenses = sumFieldCosts(financialEntries);
      const formatCurrency = value => Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
      const formatDate = value => {
        if (!value) return '-';
        const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
        return match ? `${match[3]}/${match[2]}/${match[1]}` : formatDateTime(value, '-');
      };
      let logoBase64 = null;
      try {
        const response = await fetch('/ClimoraX_vertical.png');
        const blob = await response.blob();
        logoBase64 = await new Promise(resolve => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch {}

      const sectionTitle = (text, color = '#0b8b77') => ({ text, color, fontSize: 9, bold: true, margin: [0, 10, 0, 5] });
      const headerCell = text => ({ text, bold: true, color: '#ffffff', fillColor: '#176fa8', fontSize: 7, margin: [3, 4, 3, 4] });
      const bodyCell = (text, index) => ({ text: String(text ?? '-'), color: '#29485f', fillColor: index % 2 ? '#f4f8fb' : '#ffffff', fontSize: 7, margin: [3, 4, 3, 4] });
      const content = [
        {
          columns: [
            ...(logoBase64 ? [{ image: logoBase64, width: 58, height: 58 }] : []),
            {
              stack: [
                { text: 'RELATÓRIO DO CADERNO DE CAMPO', color: '#176fa8', fontSize: 15, bold: true },
                { text: 'Ordens, tarefas, eventos históricos e despesas', color: '#607b8e', fontSize: 8, margin: [0, 3, 0, 0] },
                { text: `Gerado em ${new Date().toLocaleString('pt-BR')}`, color: '#8295a3', fontSize: 7, margin: [0, 3, 0, 0] },
              ],
              margin: [logoBase64 ? 10 : 0, 12, 0, 0],
            },
          ],
          margin: [0, 0, 0, 8],
        },
        {
          table: {
            widths: ['*', '*', '*'],
            body: [[
              { stack: [{ text: 'PROPRIEDADE', style: 'metaLabel' }, { text: propertyLabel, style: 'metaValue' }] },
              { stack: [{ text: 'TALHÃO', style: 'metaLabel' }, { text: plotLabel, style: 'metaValue' }] },
              { stack: [{ text: 'SITUAÇÃO', style: 'metaLabel' }, { text: statusLabel, style: 'metaValue' }] },
            ]],
          },
          layout: { hLineColor: () => '#d7e5ee', vLineColor: () => '#d7e5ee', paddingLeft: () => 7, paddingRight: () => 7, paddingTop: () => 7, paddingBottom: () => 7 },
        },
        sectionTitle('RESUMO DO PERÍODO'),
        {
          table: {
            widths: ['*', '*', '*', '*'],
            body: [[
              { text: `REGISTROS\n${visibleTasks.length}`, style: 'metric' },
              { text: `EM ABERTO\n${(filteredCounts.scheduled || 0) + (filteredCounts.late || 0) + (filteredCounts.in_progress || 0)}`, style: 'metric' },
              { text: `CONCLUÍDOS\n${filteredCounts.completed || 0}`, style: 'metric' },
              { text: `REALIZADO NO LOCAL\n${formatCurrency(totalExpenses)}`, style: 'metric' },
            ]],
          },
          layout: { hLineColor: () => '#cfe4df', vLineColor: () => '#cfe4df', paddingTop: () => 8, paddingBottom: () => 8 },
        },
      ];

      if (visibleTasks.length > 0) {
        content.push(
          sectionTitle('ATIVIDADES E REGISTROS'),
          {
            table: {
              headerRows: 1,
              widths: [52, '*', 76, 68, 62],
              body: [
                ['CÓDIGO', 'ATIVIDADE', 'PROPRIEDADE / TALHÃO', 'RESPONSÁVEL', 'SITUAÇÃO'].map(headerCell),
                ...visibleTasks.map((task, index) => [
                  task.id,
                  `${task.title}\n${task.due || '-'}`,
                  `${task.property}\n${task.plot}`,
                  task.responsible?.name || '-',
                  STATUS[task.status]?.label || task.status,
                ].map(value => bodyCell(value, index))),
              ],
            },
            layout: { hLineColor: () => '#dce7ee', vLineColor: () => '#dce7ee' },
          },
          sectionTitle('DETALHAMENTO DOS REGISTROS', '#176fa8'),
          ...visibleTasks.map(task => ({
            unbreakable: true,
            margin: [0, 0, 0, 7],
            table: {
              widths: ['*'],
              body: [[{
                stack: [
                  { text: `${task.id} · ${task.title}`, bold: true, color: '#176fa8', fontSize: 8.5 },
                  { text: `${task.property} · ${task.plot} · ${STATUS[task.status]?.label || task.status}`, color: '#0b8b77', fontSize: 7, bold: true, margin: [0, 2, 0, 0] },
                  { text: task.description || 'Sem descrição.', color: '#3f586a', fontSize: 7, margin: [0, 4, 0, 0] },
                  { text: `Responsável: ${task.responsible?.name || '-'}  |  Prazo/registro: ${task.due || '-'}  |  Origem: ${task.sourceLabel || '-'}`, color: '#667d8d', fontSize: 6.5, margin: [0, 3, 0, 0] },
                  { text: `Previsto total: ${formatFieldCost(summarizeTaskCosts(task, expenses).plannedTotal)} | Realizado: ${formatFieldCost(summarizeTaskCosts(task, expenses).realizedTotal)}`, color: '#3f586a', fontSize: 6.5, margin: [0, 3, 0, 0] },
                  ...(task.execution ? [{ text: `Execução: ${task.execution.summary || '-'}${task.execution.cost ? `  |  Custo: R$ ${task.execution.cost}` : ''}`, color: '#3f586a', fontSize: 6.5, margin: [0, 3, 0, 0] }] : []),
                  { text: `Evidências: ${(task.attachments || []).length} anexo(s) · ${(task.evidence || []).join(', ') || 'Nenhuma exigência informada'}`, color: '#667d8d', fontSize: 6.5, margin: [0, 3, 0, 0] },
                ],
                fillColor: '#f7fafc',
                margin: [7, 6, 7, 6],
              }]],
            },
            layout: { hLineColor: () => '#d7e5ee', vLineColor: () => '#d7e5ee' },
          }))
        );
      }

      if (visibleExpenses.length > 0) {
        content.push(
          sectionTitle('DESPESAS REGISTRADAS', '#b56a12'),
          {
            table: {
              headerRows: 1,
              widths: [58, 54, '*', 86, 60],
              body: [
                ['CÓDIGO', 'DATA', 'DESCRIÇÃO', 'PROPRIEDADE / TALHÃO', 'VALOR'].map(headerCell),
                ...visibleExpenses.map((expense, index) => {
                  const property = availableProperties.find(item => String(item.id) === String(expense.propriedade_id));
                  const plot = property?.plotRecords.find(item => plotMatchesLegacyId(item, expense.talhao_id));
                  const activity = tasks.find(task => String(task.recordId) === String(expense.ordem_id));
                  return [expense.codigo, formatDate(expense.data_despesa), `${expense.descricao}${activity ? `\nAtividade: ${activity.id}` : ''}`, `${property?.name || '-'}\n${plot?.name || 'Geral'}`, formatCurrency(expense.valor)].map(value => bodyCell(value, index));
                }),
              ],
            },
            layout: { hLineColor: () => '#eadfce', vLineColor: () => '#eadfce' },
          }
        );
      }

      const docDefinition = {
        pageSize: 'A4',
        pageMargins: [28, 24, 28, 28],
        defaultStyle: { font: 'Roboto' },
        styles: {
          metaLabel: { color: '#7d909e', fontSize: 6.5, bold: true },
          metaValue: { color: '#264b65', fontSize: 8, bold: true, margin: [0, 2, 0, 0] },
          metric: { color: '#176fa8', fontSize: 8, bold: true, alignment: 'center' },
        },
        content,
        footer: (currentPage, pageCount) => ({
          columns: [
            { text: `Página ${currentPage} de ${pageCount}`, color: '#8a9aa6', fontSize: 7, margin: [28, 8, 0, 0] },
            { text: 'ClimoraX · Caderno de Campo', color: '#176fa8', bold: true, alignment: 'right', fontSize: 7, margin: [0, 8, 28, 0] },
          ],
        }),
      };
      const safeScope = `${propertyLabel}_${plotLabel}`.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9_-]+/g, '_');
      pdfMake.createPdf(docDefinition).download(`caderno_de_campo_${safeScope}_${toLocalIsoDate()}.pdf`);
      setFeedback(`Relatório gerado com ${visibleTasks.length} registro(s) e ${visibleExpenses.length} despesa(s).`);
    } catch (error) {
      setFeedback(error?.message || 'Não foi possível gerar o relatório do Caderno de Campo.');
    } finally {
      setGeneratingReport(false);
    }
  };

  const advanceTask = async (taskId) => {
    const task = tasks.find(item => item.id === taskId);
    if (!task) return;
    setSelectedTaskId(taskId);

    if (task.isLegacyEvent) {
      setFeedback(task.isOfflineLegacyEvent
        ? 'Este evento está preservado na fila offline e será sincronizado quando a conexão retornar.'
        : 'Registro histórico aberto no Caderno sem alterar os dados originais do talhão.');
      return;
    }

    if (task.permission === 'viewer' && !task.canExecute && !task.canApprove) {
      setFeedback('Esta propriedade está disponível somente para visualização.');
      return;
    }
    if (task.status === 'awaiting' && task.canApprove === false) {
      setFeedback('É necessária permissão de aprovação nesta propriedade.');
      return;
    }
    if (['scheduled', 'late', 'in_progress'].includes(task.status) && task.canExecute === false) {
      setFeedback('A execução está atribuída a outro responsável.');
      return;
    }
    if (task.isPersisted && !cadernoRuntime.writesEnabled) {
      setFeedback('O registro desta atividade está temporariamente desativado.');
      return;
    }

    if (['completed', 'cancelled'].includes(task.status)) {
      setFeedback(task.status === 'completed'
        ? 'Registro concluído aberto com todo o histórico da atividade.'
        : 'Ordem cancelada preservada com seu histórico de rastreabilidade.');
      return;
    }
    if (task.status === 'awaiting') {
      setApprovalTaskId(taskId);
      return;
    }
    if (task.status === 'in_progress') {
      setExecutionTaskId(taskId);
      return;
    }

    if (task.isPersisted) {
      try {
        await updateCadernoOrderStatus(task.recordId, 'em_execucao');
        await loadRealOrders();
        setFeedback(`Atividade ${taskId} iniciada.`);
      } catch (error) {
        setFeedback(error?.message || 'Não foi possível iniciar a atividade.');
      }
      return;
    }

    setTasks(current => current.map(item => item.id === taskId ? {
      ...item,
      status: 'in_progress',
      dueGroup: 'today',
      updates: [...item.updates, {
        time: 'Agora',
        title: 'Execução iniciada',
        detail: `${item.responsible.name} iniciou a atividade com identidade e talhão confirmados.`,
      }],
    } : item));
    setFeedback(`Atividade ${taskId} iniciada por ${task.responsible.name}.`);
  };

  const saveTaskEdits = async (taskId, form) => {
    const selected = tasks.find(task => task.id === taskId);
    if (!selected) throw new Error('Ordem de serviço não encontrada.');
    if (selected.canEdit === false) throw new Error('Esta tarefa não pode mais ser editada neste estágio.');

    const property = availableProperties.find(item => String(item.id) === form.property);
    if (!property || !propertyActionAllowed(property, 'caderno', 'edit', ['owner', 'editor'].includes(property.permission) || !evaluationMode)) {
      throw new Error('Selecione uma propriedade com permissão de edição.');
    }
    const plot = property.plotRecords.find(item => String(item.id) === form.plot);
    if (form.plot && !plot) throw new Error('O talhão selecionado não pertence a esta propriedade.');
    const responsible = property.collaborators.find(person => String(person.id) === form.responsible);
    if (!responsible) throw new Error('Selecione um responsável autorizado nesta propriedade.');
    const locationChanged = property.name !== selected.property || (plot?.name || 'Toda a propriedade') !== selected.plot;

    const plannedAt = new Date(`${form.dueDate}T${form.dueTime || '00:00'}:00`);
    if (Number.isNaN(plannedAt.getTime())) throw new Error('Informe uma data e um horário válidos.');
    const plannedCost = form.plannedCost.trim() ? Number(form.plannedCost.replace(/\./g, '').replace(',', '.')) : null;
    if (plannedCost != null && (!Number.isFinite(plannedCost) || plannedCost < 0)) throw new Error('Informe um custo previsto válido, igual ou maior que zero.');

    if (selected.isPersisted) {
      if (!cadernoRuntime.writesEnabled) throw new Error('As gravações reais estão bloqueadas neste ambiente.');
      await updateCadernoOrder(selected.recordId, {
        propriedade_id: property.id,
        talhao_id: plot?.id || null,
        custo_previsto: plannedCost,
        titulo: form.title.trim(),
        descricao: form.description.trim() || null,
        tipo: UI_TYPE_TO_DATABASE[form.type] || 'operacao',
        prioridade: UI_PRIORITY[form.priority] || 'normal',
        responsavel_id: form.responsible,
        inicio_previsto: plannedAt.toISOString(),
        evidencias_exigidas: form.evidence
          ? ['Foto ou documento', 'Observação de conclusão']
          : ['Registro de conclusão'],
      }, selected.version);
      await loadRealOrders();
    } else {
      setTasks(current => current.map(task => task.id === taskId ? {
        ...task,
        propertyId: property.id,
        property: property.name,
        plotId: plot?.id || null,
        plot: plot?.name || 'Toda a propriedade',
        location: property.location,
        permission: property.permission,
        title: form.title.trim(),
        description: form.description.trim() || 'Sem instruções adicionais.',
        type: form.type,
        priority: form.priority,
        responsible,
        plannedAt: plannedAt.toISOString(),
        plannedCost,
        due: formatDateTime(plannedAt.toISOString()),
        evidence: form.evidence ? ['Foto ou documento', 'Observação de conclusão'] : ['Registro de conclusão'],
        version: (task.version || 1) + 1,
        updates: [{ time: 'Agora', title: 'Ordem editada', detail: locationChanged ? `${currentUserName} alterou o local de ${selected.property} · ${selected.plot} para ${property.name} · ${plot?.name || 'Toda a propriedade'}.` : `${currentUserName} atualizou os dados da atividade.` }, ...task.updates],
      } : task));
    }

    if (locationChanged) {
      setPropertyFilter(current => current === 'all' ? current : property.name);
      setPlotFilter('all');
    }
    setEditingTaskId(null);
    setSelectedTaskId(taskId);
    setFeedback(`${taskId} atualizada. A versão anterior permanece no histórico.`);
  };

  const cancelTask = async (taskId, reason) => {
    const selected = tasks.find(task => task.id === taskId);
    if (!selected) throw new Error('Ordem de serviço não encontrada.');
    if (selected.canCancel === false) throw new Error('Esta tarefa não pode ser excluída.');

    if (selected.isPersisted) {
      if (!cadernoRuntime.writesEnabled) throw new Error('As gravações reais estão bloqueadas neste ambiente.');
      await cancelCadernoOrder(selected.recordId, reason, selected.version);
      await loadRealOrders();
    } else {
      setTasks(current => current.map(task => task.id === taskId ? {
        ...task,
        status: 'cancelled',
        databaseStatus: 'cancelada',
        cancellationReason: reason,
        canEdit: false,
        canCancel: false,
        canDelete: task.canDelete,
        canAttach: false,
        updates: [{ time: 'Agora', title: 'Tarefa excluída da operação', detail: `${currentUserName}: ${reason}` }, ...task.updates],
      } : task));
    }

    setCancellingTaskId(null);
    setSelectedTaskId(null);
    setFilter('all');
    setFeedback(`${taskId} retirada da lista ativa. Ela continua disponível em “Canceladas”.`);
  };

  const deleteTask = async taskId => {
    const selected = tasks.find(task => task.id === taskId);
    if (!selected) throw new Error('Atividade não encontrada.');
    if (!selected.canDelete) throw new Error('É necessário ter permissão de editar e excluir nesta propriedade.');

    let warning = '';
    if (selected.isOfflineLegacyEvent) {
      removeFromQueue(selected.recordId);
      await loadRealOrders();
    } else if (selected.isPersisted || selected.isLegacyEvent) {
      if (!cadernoRuntime.writesEnabled) throw new Error('As gravações reais estão bloqueadas neste ambiente.');
      warning = selected.isLegacyEvent
        ? await deleteLegacyCadernoEvent(selected.recordId)
        : await deleteCadernoOrder(selected.recordId);
      await loadRealOrders();
    } else {
      setTasks(current => current.filter(task => task.id !== taskId));
    }
    setExpenses(current => current.filter(expense => String(expense.ordem_id) !== String(selected.recordId || selected.id)));

    setDeletingTaskId(null);
    setSelectedTaskId(null);
    setFeedback(warning || `${taskId} excluída definitivamente com seus dados vinculados.`);
  };

  const attachFilesToTask = async (taskId, evidenceFiles) => {
    const selected = tasks.find(task => task.id === taskId);
    if (!selected) throw new Error('Ordem de serviço não encontrada.');
    if (selected.canAttach === false) throw new Error('Seu usuário não pode anexar arquivos a esta tarefa.');

    if (selected.isPersisted) {
      if (!cadernoRuntime.writesEnabled) throw new Error('As gravações reais estão bloqueadas neste ambiente.');
      for (const evidence of evidenceFiles) {
        const path = await uploadCadernoEvidenceFile({
          propertyId: selected.propertyId,
          orderId: selected.recordId,
          evidenceId: evidence.id,
          file: evidence.file,
          safeName: evidence.safeName,
          mimeType: evidence.mimeType,
        });
        await createCadernoEvidence({
          id: evidence.id,
          ordem_id: selected.recordId,
          execucao_id: null,
          tipo: evidence.type,
          titulo: `${evidence.type === 'foto' ? 'Foto' : 'Documento'} · ${evidence.name}`.slice(0, 160),
          arquivo_path: path,
          nome_original: evidence.name,
          mime_type: evidence.mimeType,
          mime_type_original: evidence.originalMimeType,
          tamanho_bytes: evidence.size,
          tamanho_original_bytes: evidence.originalSize,
          compressao: evidence.compression,
          hash_sha256: evidence.hashSha256,
          capturado_em: evidence.capturedAt,
        });
      }
      await loadRealOrders();
    } else {
      const attachments = evidenceFiles.map(evidence => ({
        id: evidence.id,
        type: evidence.type,
        name: evidence.name,
        size: evidence.size,
        originalSize: evidence.originalSize,
        compression: evidence.compression,
      }));
      setTasks(current => current.map(task => task.id === taskId ? {
        ...task,
        attachments: [...(task.attachments || []), ...attachments],
        updates: [{ time: 'Agora', title: 'Anexos adicionados', detail: `${evidenceFiles.length} arquivo(s) compactado(s) e vinculados à tarefa.` }, ...task.updates],
      } : task));
    }

    setAttachmentTaskId(null);
    setSelectedTaskId(taskId);
    setFeedback(`${evidenceFiles.length} arquivo(s) anexado(s) com compactação e assinatura digital.`);
  };

  const downloadAttachment = async attachment => {
    if (attachment?.legacyUrl) {
      try {
        const legacyUrl = new URL(attachment.legacyUrl, window.location.origin);
        if (!['http:', 'https:'].includes(legacyUrl.protocol)) throw new Error('Endereço de anexo legado inválido.');
        const link = document.createElement('a');
        link.href = legacyUrl.href;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        link.remove();
        setFeedback(`${attachment.name} aberto a partir do registro histórico preservado.`);
      } catch (error) {
        setFeedback(error?.message || 'Não foi possível abrir o anexo histórico.');
      }
      return;
    }
    if (!attachment?.path) return;
    setFeedback(`Preparando ${attachment.name} para download…`);
    try {
      let blob = await downloadCadernoEvidenceFile(attachment.path);
      let fileName = attachment.name || 'anexo';

      if (attachment.compression === 'gzip') {
        if (typeof DecompressionStream === 'undefined') {
          throw new Error('Este navegador não consegue descompactar o documento. Atualize o navegador e tente novamente.');
        }
        const stream = blob.stream().pipeThrough(new DecompressionStream('gzip'));
        blob = new Blob([await new Response(stream).arrayBuffer()], { type: attachment.originalMimeType || 'application/octet-stream' });
      } else if (attachment.compression === 'webp-82' && !/\.webp$/i.test(fileName)) {
        fileName = fileName.replace(/\.[^.]+$/, '') + '.webp';
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setFeedback(`${fileName} baixado com segurança.`);
    } catch (error) {
      setFeedback(error?.message || 'Não foi possível baixar o anexo.');
    }
  };

  const submitExecution = async (taskId, form) => {
    const selected = tasks.find(task => task.id === taskId);
    if (!selected) throw new Error('Ordem de serviço não encontrada.');

    const [year, month, day] = form.actualDate.split('-');
    const dateLabel = day && month ? `${day}/${month}/${year}` : 'Hoje';
    const photoCount = form.evidenceFiles.filter(item => item.type === 'foto').length;
    const documentCount = form.evidenceFiles.filter(item => item.type === 'documento').length;
    const evidenceCount = form.evidenceFiles.length;

    if (selected.isPersisted) {
      if (!cadernoRuntime.writesEnabled) {
        throw new Error('As gravações reais permanecem bloqueadas neste ambiente.');
      }

      const startAt = new Date(`${form.actualDate}T${form.startTime || '00:00'}:00`);
      const finishAt = new Date(`${form.actualDate}T${form.endTime || form.startTime || '00:00'}:00`);
      if (Number.isNaN(startAt.getTime()) || Number.isNaN(finishAt.getTime()) || finishAt < startAt) {
        throw new Error('Confira a data e os horários. O término não pode ser anterior ao início.');
      }

      const irrigationDetails = selected.type === 'Irrigação'
        ? [
            form.appliedAmount ? `Lâmina aplicada: ${form.appliedAmount} mm.` : '',
            form.finalReading ? `Leitura final: ${form.finalReading}.` : '',
          ].filter(Boolean).join(' ')
        : '';
      const notes = [form.notes.trim(), irrigationDetails].filter(Boolean).join('\n');
      const location = form.location || {};

      setFeedback(`Preparando ${evidenceCount} evidência(s) para a ${taskId}…`);
      try {
        const execution = await createCadernoExecution({
          ordem_id: selected.recordId,
          client_request_id: form.submissionId,
          status: 'iniciada',
          inicio_em: startAt.toISOString(),
          resumo: form.summary.trim(),
          observacoes: notes || null,
          custo_real: null,
          latitude: location.latitude ?? null,
          longitude: location.longitude ?? null,
          precisao_metros: location.accuracy ?? null,
        });

        for (const evidence of form.evidenceFiles) {
          const path = await uploadCadernoEvidenceFile({
            propertyId: selected.propertyId,
            orderId: selected.recordId,
            evidenceId: evidence.id,
            file: evidence.file,
            safeName: evidence.safeName,
            mimeType: evidence.mimeType,
          });

          await createCadernoEvidence({
            id: evidence.id,
            ordem_id: selected.recordId,
            execucao_id: execution.id,
            tipo: evidence.type,
            titulo: `${evidence.type === 'foto' ? 'Foto' : 'Documento'} · ${evidence.name}`.slice(0, 160),
            arquivo_path: path,
            nome_original: evidence.name,
            mime_type: evidence.mimeType,
            mime_type_original: evidence.originalMimeType,
            tamanho_bytes: evidence.size,
            tamanho_original_bytes: evidence.originalSize,
            compressao: evidence.compression,
            hash_sha256: evidence.hashSha256,
            capturado_em: evidence.capturedAt,
            latitude: location.latitude ?? null,
            longitude: location.longitude ?? null,
            precisao_metros: location.accuracy ?? null,
          });
        }

        await updateCadernoExecutionStatus(execution.id, 'finalizada', {
          fim_em: finishAt.toISOString(),
        });
        await loadRealOrders();
        setExecutionTaskId(null);
        setSelectedTaskId(taskId);
        setFilter('all');
        setFeedback(`Execução da ${taskId} registrada com ${evidenceCount} evidência(s) privadas e enviada para aprovação.`);
        return;
      } catch (error) {
        setFeedback('O envio não foi concluído. A identificação da tentativa foi preservada para uma repetição segura.');
        throw error;
      }
    }

    setTasks(current => current.map(task => task.id === taskId ? {
      ...task,
      status: 'awaiting',
      due: `Executada em ${dateLabel} · ${form.endTime || 'horário não informado'}`,
      dueGroup: 'approval',
      execution: {
        summary: form.summary.trim(),
        notes: form.notes.trim(),
        actualPeriod: `${dateLabel} · ${form.startTime || '--:--'}–${form.endTime || '--:--'}`,
        photos: photoCount,
        document: documentCount > 0,
        locationConfirmed: Boolean(form.location),
        cost: null,
        executor: task.responsible.name,
        appliedAmount: form.appliedAmount,
        finalReading: form.finalReading,
      },
      updates: [...task.updates, {
        time: 'Agora',
        title: 'Execução enviada para aprovação',
        detail: `${task.responsible.name} registrou ${evidenceCount} evidência(s) e o período executado.`,
      }],
    } : task));
    setExecutionTaskId(null);
    setSelectedTaskId(taskId);
    setFilter('all');
    setFeedback(`Execução da ${taskId} enviada ao gestor para revisão.`);
  };

  const reviewTask = async (taskId, approved, note) => {
    const selected = tasks.find(task => task.id === taskId);
    if (!selected) throw new Error('Ordem de serviço não encontrada.');

    if (selected.isPersisted) {
      if (!cadernoRuntime.writesEnabled) {
        throw new Error('As gravações reais permanecem bloqueadas neste ambiente.');
      }
      if (!note.trim()) {
        throw new Error('Informe o parecer do proprietário antes de concluir a revisão.');
      }

      try {
        await updateCadernoOrderStatus(
          selected.recordId,
          approved ? 'concluida' : 'em_execucao',
          { parecer_aprovacao: note.trim() }
        );
        await loadRealOrders();
        setApprovalTaskId(null);
        setSelectedTaskId(taskId);
        setFilter('all');
        setFeedback(approved
          ? `${taskId} aprovada pelo proprietário e incorporada ao histórico.`
          : `${taskId} devolvida ao responsável com parecer registrado.`);
      } catch (error) {
        setFeedback(error?.message || 'Não foi possível concluir a revisão.');
        throw error;
      }
      return;
    }

    setTasks(current => current.map(task => task.id === taskId ? {
      ...task,
      status: approved ? 'completed' : 'in_progress',
      due: approved ? `Concluída e aprovada · Agora` : 'Correção solicitada · Agora',
      dueGroup: approved ? 'completed' : 'today',
      updates: [...task.updates, {
        time: 'Agora',
        title: approved ? 'Atividade aprovada e registrada no histórico do talhão' : 'Execução devolvida para correção',
        detail: `${note.trim() || (approved ? 'Execução e evidências conferidas.' : 'O gestor solicitou ajustes no registro.')} · Gestor: ${currentUserName}.`,
      }],
    } : task));
    setApprovalTaskId(null);
    setSelectedTaskId(taskId);
    setFilter('all');
    setFeedback(approved
      ? `${taskId} aprovada e incorporada ao histórico do talhão.`
      : `${taskId} devolvida ao responsável para correção.`);
  };

  const createTask = async (form, fromAlert, requestedMode = 'order') => {
    const resolvedMode = fromAlert ? 'order' : requestedMode;
    const creationCopy = getFieldCreationMode(resolvedMode);
    const property = availableProperties.find(item => String(item.id) === form.property) || availableProperties[0] || PROPERTIES[0];
    const responsibleOptions = property.collaborators || [];
    const responsible = responsibleOptions.find(person => String(person.id) === form.responsible) || responsibleOptions[0];
    const plotRecord = form.plot
      ? property.plotRecords?.find(item => String(item.id) === form.plot)
      : null;

    if (!responsible || (form.plot && !plotRecord)) {
      setFeedback('Selecione uma propriedade e um responsável autorizado para criar a ordem.');
      return;
    }

    if (usingRealCaderno) {
      if (!cadernoRuntime.writesEnabled) {
        setFeedback('A ordem não foi salva: a criação de ordens está temporariamente desativada.');
        return;
      }
      if (!property?.id || !currentUserId) {
        setFeedback('Não foi possível confirmar propriedade e usuário autenticado para esta ordem.');
        return;
      }

      const plannedAt = `${form.dueDate}T${form.dueTime || '00:00'}:00`;
      try {
        await createCadernoOrder({
          propriedade_id: property.id,
          talhao_id: plotRecord?.id || null,
          titulo: form.title.trim(),
          descricao: form.description.trim() || null,
          tipo: UI_TYPE_TO_DATABASE[form.type] || 'operacao',
          status: 'planejada',
          prioridade: UI_PRIORITY[form.priority] || 'normal',
          origem_tipo: fromAlert ? 'alerta' : 'manual',
          origem_referencia: fromAlert ? sourceAlert : null,
          origem_dados: fromAlert
            ? { alerta_id: sourceAlert, entry_kind: 'order' }
            : { entry_kind: resolvedMode },
          responsavel_id: responsible.id,
          inicio_previsto: new Date(plannedAt).toISOString(),
          exige_aprovacao: resolvedMode === 'order',
          evidencias_exigidas: form.evidence
            ? ['Foto ou documento', 'Observação de conclusão']
            : ['Registro de conclusão'],
        });
        await loadRealOrders();
        setFilter('all');
        setPropertyFilter('all');
        setShowCreate(false);
        clearAlertContext();
        const notice = `${creationCopy.successNoun} criada com sucesso.`;
        setFeedback(notice);
        returnToOrigin(notice, 'order');
      } catch (error) {
        setFeedback(error?.message || `Não foi possível criar ${resolvedMode === 'task' ? 'a tarefa' : 'a ordem de serviço'}.`);
      }
      return;
    }

    const id = `${resolvedMode === 'task' ? 'TAR' : 'OS'}-2026-${String(43 + tasks.length).padStart(4, '0')}`;
    const createdTask = {
      id,
      title: form.title,
      type: form.type,
      property: property.name,
      location: property.location,
      plot: plotRecord?.name || 'Toda a propriedade',
      permission: property.permission || 'owner',
      status: 'scheduled',
      priority: form.priority,
      due: `${form.dueDate.split('-').reverse().join('/')} · ${form.dueTime || 'Sem horário'}`,
      dueGroup: 'upcoming',
      source: fromAlert ? 'alert' : 'manual',
      sourceLabel: fromAlert ? 'Alerta operacional' : resolvedMode === 'task' ? 'Tarefa simples' : 'Ordem criada manualmente',
      responsible,
      createdBy: currentUserName,
      description: form.description || 'Sem instruções adicionais.',
      evidence: form.evidence ? ['Foto ou documento', 'Observação de conclusão'] : ['Registro de conclusão'],
      updates: [
        { time: 'Agora', title: fromAlert ? 'Ordem criada a partir de alerta' : resolvedMode === 'task' ? 'Tarefa criada manualmente' : 'Ordem criada manualmente', detail: `${currentUserName} vinculou a atividade a ${property.name}${plotRecord ? ` · ${plotRecord.name}` : ' · toda a propriedade'}.` },
        { time: 'Agora', title: 'Responsável atribuído', detail: `${responsible.name} foi vinculado à atividade. A pendência foi adicionada somente à central interna desta avaliação.` },
      ],
    };
    createLocalOperationalNotification({
      recipientId: responsible.id,
      eventKey: `order-assigned:${id}:${responsible.id}`,
      type: 'task_assigned',
      title: 'Nova atividade atribuída',
      message: createdTask.title,
      priority: form.priority,
      propertyId: property.id,
      propertyName: property.name,
      plotId: plotRecord?.id || null,
      plotName: plotRecord?.name || 'Toda a propriedade',
      orderId: id,
      action: 'caderno',
    });
    setTasks(current => [createdTask, ...current]);
    setSelectedTaskId(id);
    setFilter('all');
    setPropertyFilter('all');
    setShowCreate(false);
    clearAlertContext();
    const notice = `${creationCopy.successNoun} ${id} criada com propriedade, talhão e responsável definidos.`;
    setFeedback(notice);
    returnToOrigin(notice, 'order');
  };

  const createExpense = async form => {
    const property = expenseProperties.find(item => String(item.id) === form.property);
    const plotRecord = form.plot
      ? property?.plotRecords?.find(item => String(item.id) === form.plot)
      : null;

    if (!property) throw new Error('Selecione uma propriedade autorizada para a despesa.');
    if (form.plot && !plotRecord) throw new Error('O talhão selecionado não pertence a esta propriedade.');
    const activityPlot = expenseTask ? property.plotRecords.find(plot => plotMatchesLegacyId(plot, expenseTask.plotId) || plot.name === expenseTask.plot) : null;
    if (expenseTask && (String(expenseTask.propertyId || property.id) !== String(property.id)
      || String(activityPlot?.id || '') !== String(plotRecord?.id || ''))) {
      throw new Error('O gasto deve permanecer no local da atividade.');
    }
    const activityFields = expenseTask ? {
      ordem_id: expenseTask.recordId || expenseTask.id,
      categoria: form.category,
      adicional_ao_legado: form.additionalToLegacy,
    } : {};
    if (!usingRealCaderno) {
      if (!evaluationMode) {
        setExpenses(current => [...current, {
          id: form.submissionId,
          codigo: 'DEMO',
          propriedade_id: property.id,
          talhao_id: plotRecord?.id || null,
          valor: parseCadernoExpenseValue(form.value),
          descricao: form.description.trim(),
          data_despesa: form.expenseDate,
          ...activityFields,
        }]);
        const notice = `Despesa demonstrativa registrada em ${property.name}${plotRecord ? ` · ${plotRecord.name}` : ' · geral da propriedade'}. Nenhum dado real foi gravado.`;
        setShowExpense(false);
        setFeedback(notice);
        returnToOrigin(notice, 'expense');
        return;
      }
      throw new Error('Despesas reais só podem ser registradas na sessão autenticada do Caderno de Campo.');
    }

    try {
      const expense = await createCadernoExpense({
        propriedade_id: property.id,
        talhao_id: plotRecord?.id || null,
        valor: form.value,
        descricao: form.description,
        data_despesa: form.expenseDate,
        client_request_id: form.submissionId,
        ...activityFields,
      });
      setExpenses(current => [...current.filter(item => item.id !== expense.id), expense]);
      await loadRealOrders();

      if (form.receipt) {
        try {
          const path = await uploadCadernoExpenseReceipt({
            propertyId: property.id,
            expenseId: expense.id,
            receipt: form.receipt,
          });
          await attachCadernoExpenseReceipt(expense.id, form.receipt, path);
        } catch (receiptError) {
          setShowExpense(false);
          const notice = `Despesa ${expense.codigo} registrada, mas o comprovante não foi anexado: ${receiptError?.message || 'falha no envio.'}`;
          setFeedback(notice);
          returnToOrigin(notice, 'expense');
          return;
        }
      }

      setShowExpense(false);
      const notice = `Despesa ${expense.codigo} registrada em ${property.name}${plotRecord ? ` · ${plotRecord.name}` : ' · geral da propriedade'}${form.receipt ? ', com comprovante privado.' : '.'}`;
      setFeedback(notice);
      returnToOrigin(notice, 'expense');
    } catch (error) {
      if (isCadernoExpenseSchemaUnavailable(error)) {
        const setupError = new Error('A estrutura segura de despesas ainda não foi instalada no banco. Nenhum dado foi alterado.');
        setFeedback(setupError.message);
        throw setupError;
      }
      setFeedback(error?.message || 'Não foi possível registrar a despesa.');
      throw error;
    }
  };

  return (
    <div className={`field-notebook-shell ${darkMode ? 'is-dark' : ''}`}>
      <header className="field-notebook-topbar">
        <button type="button" className="field-notebook-icon-button" onClick={() => onNavigate('dashboard')} aria-label="Voltar ao painel"><FaArrowLeft /></button>
        <div className="field-notebook-brand">
          <span><FaClipboardCheck /></span>
          <div><strong>Caderno de Campo</strong><small>Ordens, despesas e rastreabilidade</small></div>
        </div>
        <div className="field-notebook-account"><FaShieldAlt /><span><strong>{accountLabel}</strong><small>{usingRealCaderno ? 'Sessão real · conectado ao banco de produção' : 'Ambiente isolado da conta'}</small></span></div>
        <button type="button" className="field-notebook-icon-button" onClick={() => setDarkMode(value => !value)} aria-label={darkMode ? 'Ativar tema claro' : 'Ativar tema escuro'}>
          {darkMode ? <FaSun /> : <FaMoon />}
        </button>
      </header>

      <main className="field-notebook-main">
        {evaluationMode && (realContextLoading || realContextError || realOrdersLoading || realOrdersError || legacyEventsError) && (
          <div className="field-notebook-evaluation" role="status">
            <FaShieldAlt />
            <div>
              <span>
                {realContextLoading
                  ? 'Carregando as propriedades e os talhões autorizados para esta conta…'
                  : realContextError
                    ? `Não foi possível carregar suas propriedades: ${realContextError}`
                    : realOrdersLoading
                      ? 'Carregando suas ordens de serviço e seus eventos históricos…'
                      : realOrdersError
                        ? `Não foi possível carregar as ordens: ${realOrdersError}`
                        : `As ordens foram carregadas, mas os eventos históricos precisam de atenção: ${legacyEventsError}`}
              </span>
            </div>
          </div>
        )}
        <section className="field-notebook-hero">
          <div>
            <span className="field-notebook-kicker">Gestão da execução agrícola</span>
            <h1>Planeje, atribua e comprove cada atividade no campo</h1>
            <p>Tarefas, alertas, planejamento e despesas rápidas reunidos com contexto de propriedade, talhão e responsável.</p>
            <div className="field-notebook-hero-actions">
              <button type="button" onClick={() => openCreate('task')} title="Criar uma atividade simples ou com aprovação"><FaPlus /> Nova atividade</button>
              <button type="button" className="is-expense" onClick={openExpense}><FaDollarSign /> Adicionar despesa</button>
            </div>
            <p className="field-notebook-creation-help">
              Comece de forma simples. Ative a aprovação somente quando a atividade precisar de conferência do proprietário.
            </p>
          </div>

          <div className="field-notebook-metrics" aria-label="Resumo das ordens de serviço">
            <div><span>Para hoje</span><strong>{counts.today}</strong><small>atividades</small></div>
            <div className="is-late"><span>Atrasadas</span><strong>{counts.late}</strong><small>exigem ação</small></div>
            <div className="is-approval"><span>Para aprovar</span><strong>{counts.approval}</strong><small>com evidências</small></div>
            <div className="is-complete"><span>Concluídas</span><strong>{counts.completed}</strong><small>neste ciclo</small></div>
          </div>
        </section>

        {feedback && <div className="field-notebook-feedback"><FaCheckCircle /><span>{feedback}</span><button type="button" onClick={() => setFeedback('')} aria-label="Fechar aviso"><FaTimes /></button></div>}
        {cleanupError && <div className="field-notebook-feedback" role="alert"><FaExclamationTriangle /><span>Não foi possível concluir a limpeza de arquivos excluídos: {cleanupError}</span><button type="button" onClick={loadRealOrders} disabled={realOrdersLoading}>Tentar novamente</button></div>}

        {!operationActive && tasks.length === 0 ? (
          <section className="field-notebook-empty">
            <span><FaClipboardCheck /></span>
            <small className="field-notebook-kicker">Primeiro acesso</small>
            <h2>Crie a primeira atividade da sua operação</h2>
            <p>Informe o que precisa ser feito, quem fará e quando. Os detalhes adicionais são opcionais.</p>
            <button type="button" onClick={() => openCreate('task')}><FaPlus /> Criar primeira atividade</button>
            <div>
              <span><FaUser /><strong>Manual</strong><small>Criada por um gestor</small></span>
              <span><FaBell /><strong>Alerta</strong><small>Gerada por dados de campo</small></span>
              <span><FaCalendarAlt /><strong>Planejamento</strong><small>Programada ou recorrente</small></span>
            </div>
          </section>
        ) : (
          <div className="field-notebook-layout">
            <section className="field-notebook-board">
              <div className="field-notebook-board-heading">
                <div><span className="field-notebook-kicker">Operação organizada</span><h2>Atividades</h2></div>
                <div className="field-notebook-board-actions">
                  <button type="button" className="is-report" onClick={exportFieldNotebookReport} disabled={generatingReport || realOrdersLoading} title="Exportar os registros conforme os filtros selecionados">
                    <FaFilePdf /> {generatingReport ? 'Gerando…' : 'Exportar relatório'}
                  </button>
                  <button type="button" onClick={() => openCreate('task')} title="Criar uma atividade simples ou com aprovação"><FaPlus /> Nova atividade</button>
                </div>
              </div>

              <div className="field-notebook-controls">
                <div className="field-notebook-filters" aria-label="Filtrar tarefas">
                  {FILTERS.map(item => (
                    <button type="button" key={item.key} className={filter === item.key ? 'is-active' : ''} aria-pressed={filter === item.key} onClick={() => setFilter(item.key)}>{item.label}</button>
                  ))}
                </div>
                <label>
                  <FaMapMarkerAlt />
                  <select aria-label="Filtrar por propriedade" value={propertyFilter} onChange={event => {
                    setPropertyFilter(event.target.value);
                    setPlotFilter('all');
                  }}>
                    <option value="all">Todas as propriedades</option>
                    {availableProperties.map(item => <option key={item.id} value={item.name}>{item.name} · {PERMISSION_LABELS[item.permission] || 'Acesso'}</option>)}
                  </select>
                </label>
                <label>
                  <FaSeedling />
                  <select aria-label="Filtrar por talhão" value={plotFilter} onChange={event => setPlotFilter(event.target.value)} disabled={plotFilterOptions.length === 0}>
                    <option value="all">{plotFilterOptions.length > 0 ? 'Todos os talhões' : 'Nenhum talhão disponível'}</option>
                    {plotFilterOptions.map(item => (
                      <option key={item.value} value={item.value}>
                        {item.plotName}{propertyFilter === 'all' ? ` · ${item.propertyName}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="field-notebook-list">
                {visibleTasks.length > 0 ? visibleTasks.map(task => (
                  <TaskCard key={task.id} task={task} selected={selectedTaskId === task.id} onSelect={selectTask} onAdvance={advanceTask} />
                )) : (
                  <div className="field-notebook-no-results"><FaCheckCircle /><strong>Nenhuma atividade neste filtro</strong><span>A operação está em dia nesta categoria.</span></div>
                )}
              </div>
            </section>

            {selectedTask ? (
              <TaskDetail
                task={selectedTask}
                accountLabel={accountLabel}
                expenses={expenses}
                expensesError={expensesError}
                canAddExpense={!expensesError && !selectedTask.isLegacyEvent && selectedTask.status !== 'cancelled' && expenseProperties.some(property => String(property.id) === String(selectedTask.propertyId) || property.name === selectedTask.property)}
                canDeleteExpense={!expensesError && !selectedTask.isLegacyEvent && selectedTask.canDelete}
                onDeleteExpense={setDeletingExpense}
                onSpend={task => {
                  if (openExpense()) setExpenseTaskId(task.id);
                }}
                onClose={() => setSelectedTaskId(null)}
                onAdvance={advanceTask}
                onEdit={task => setEditingTaskId(task.id)}
                onCancel={task => setCancellingTaskId(task.id)}
                onDelete={task => setDeletingTaskId(task.id)}
                onAttach={task => setAttachmentTaskId(task.id)}
                onDownload={downloadAttachment}
                detailRef={taskDetailRef}
              />
            ) : (
              <aside className="field-task-detail is-placeholder">
                <FaHistory /><strong>Selecione uma atividade</strong><span>Veja autor, responsável, evidências e todo o histórico da execução.</span>
              </aside>
            )}
          </div>
        )}
        {evaluationMode && pecuariaEnabled && <section className="pec-history">
          <button type="button" aria-expanded={showPecuariaHistory} onClick={() => setShowPecuariaHistory(value => !value)}><FaHistory /> {showPecuariaHistory ? 'Recolher histórico pecuário' : 'Ver histórico pecuário'}</button>
          {showPecuariaHistory && <PecuariaHistory
                propertyId={plotFilterOptions.find(p => p.value === plotFilter)?.propertyId || availableProperties.find(p => p.name === propertyFilter)?.id || null}
                plotId={plotFilterOptions.find(p => p.value === plotFilter)?.physicalPlotId || null}
                legacyPlotId={plotFilterOptions.find(p => p.value === plotFilter)?.plotId || null}
                onNavigate={onNavigate} />}
        </section>}
      </main>

      {showCreate && editableProperties.length > 0 && (
        <CreateTaskModal
          sourceAlert={sourceAlert}
          creationMode={creationMode}
          properties={editableProperties}
          initialPropertyId={initialPropertyId}
          initialPlotId={initialPlotId}
          onClose={closeCreate}
          onCreate={createTask}
          willReturnToMap={initialAction === 'new-order' && returnTarget === 'sensoriamento'}
        />
      )}
      {executionTask && <ExecutionModal task={executionTask} accountLabel={accountLabel} onClose={() => setExecutionTaskId(null)} onSubmit={submitExecution} />}
      {editingTask && (
        <EditTaskModal
          task={editingTask}
          property={editingProperty}
          properties={availableProperties.filter(property => propertyActionAllowed(property, 'caderno', 'edit', ['owner', 'editor'].includes(property.permission) || !evaluationMode))}
          onClose={() => setEditingTaskId(null)}
          onSave={saveTaskEdits}
        />
      )}
      {showExpense && expenseProperties.length > 0 && (
        <QuickExpenseModal
          key={expenseTaskId || 'general'}
          properties={expenseProperties}
          task={expenseTask}
          legacyTotal={expenseTask ? summarizeTaskCosts(expenseTask, expenses).legacyTotal : 0}
          initialPropertyId={expenseTask?.propertyId || expenseProperties.find(property => property.name === expenseTask?.property)?.id || initialPropertyId}
          initialPlotId={expenseTask?.plotId || expenseProperties.find(property => property.name === expenseTask?.property)?.plotRecords.find(plot => plot.name === expenseTask?.plot)?.id || initialPlotId}
          onClose={() => {
            setShowExpense(false);
            setExpenseTaskId(null);
            if (initialAction === 'new-expense') returnToOrigin('', 'expense-cancelled');
          }}
          onCreate={createExpense}
          willReturnToMap={returnTarget === 'sensoriamento'}
        />
      )}
      {deletingExpense && <DeleteExpenseModal expense={deletingExpense} onClose={() => setDeletingExpense(null)} onConfirm={async expense => {
        let warning = '';
        if (usingRealCaderno) {
          warning = await deleteCadernoExpense(expense.id);
          await loadRealOrders();
        } else {
          if (evaluationMode) throw new Error('Exclusão real exige uma sessão autenticada.');
          setExpenses(current => current.filter(item => item.id !== expense.id));
        }
        setDeletingExpense(null);
        setFeedback(warning || 'Gasto excluído definitivamente. Você pode registrar o valor correto.');
      }} />}
      {cancellingTask && (
        <CancelTaskModal
          task={cancellingTask}
          onClose={() => setCancellingTaskId(null)}
          onConfirm={cancelTask}
        />
      )}
      {deletingTask && (
        <DeleteTaskModal
          task={deletingTask}
          onClose={() => setDeletingTaskId(null)}
          onConfirm={deleteTask}
        />
      )}
      {attachmentTask && (
        <TaskAttachmentModal
          task={attachmentTask}
          onClose={() => setAttachmentTaskId(null)}
          onSubmit={attachFilesToTask}
        />
      )}
      {approvalTask && (
        <ApprovalModal
          task={approvalTask}
          onClose={() => setApprovalTaskId(null)}
          onApprove={(taskId, note) => reviewTask(taskId, true, note)}
          onReturn={(taskId, note) => reviewTask(taskId, false, note)}
        />
      )}
    </div>
  );
}
